/** Internal type. DO NOT USE DIRECTLY. */
type Exact<T extends { [key: string]: unknown }> = { [K in keyof T]: T[K] }
/** Internal type. DO NOT USE DIRECTLY. */
export type Incremental<T> = T | { [P in keyof T]?: P extends ' $fragmentName' | '__typename' ? T[P] : never }
import { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core'
export type AdminShapeNameConversionUpsertInput = {
  field: ShapeNameConversionFieldEnum
  id?: string | number | null | undefined
  normalizedValue: string
  sourceValue: string
  tenantId: string
}

export type AdminTenantCreateInput = {
  companyName: string
  introductionDate?: string | null | undefined
  lastConversationDate?: string | null | undefined
  lastInterviewDate?: string | null | undefined
  memo: string
  name: string
  planId: string
  prefecture: string
  quoteNumber: string
}

export type AdminTenantSettingUpdateInput = {
  orderLineMatchingEnabled?: boolean | null | undefined
  orderMatchingEnabled?: boolean | null | undefined
  scrmSiteCode?: string | null | undefined
  tenantId: string
}

export type AdminTenantUpdateInput = {
  companyName: string
  id: string | number
  introductionDate?: string | null | undefined
  lastConversationDate?: string | null | undefined
  lastInterviewDate?: string | null | undefined
  memo: string
  name: string
  planId?: string | null | undefined
  prefecture: string
  quoteNumber?: string | null | undefined
  status: TenantStatusEnum
}

export type ArchiveAdminIdentityInput = {
  id: string | number
}

export type CreateAdminIdentityInvitationInput = {
  email: string
}

export type CreateAdminInvitationInput = {
  email: string
  name: string
  role: UserRoleEnum
  smsMfaRequired: boolean
  tenantId: string
}

export type CreateInvitationInput = {
  email: string
  name: string
  role: UserRoleEnum
  smsMfaRequired: boolean
}

export type DeleteOrderLineMatchingOrderInput = {
  orderId: string | number
}

export const IdentityRoleEnum = {
  Admin: 'admin',
  Member: 'member',
} as const

export type IdentityRoleEnum = (typeof IdentityRoleEnum)[keyof typeof IdentityRoleEnum]
/** ミルシートPDF自動紐付けの結果ステータス */
export const MillsheetAutoLinkStatusEnum = {
  AmbiguousCertificateNo: 'AMBIGUOUS_CERTIFICATE_NO',
  AmbiguousOrderNumber: 'AMBIGUOUS_ORDER_NUMBER',
  Linked: 'LINKED',
  LinkedByCertificateNoOnly: 'LINKED_BY_CERTIFICATE_NO_ONLY',
  NoMatch: 'NO_MATCH',
} as const

export type MillsheetAutoLinkStatusEnum = (typeof MillsheetAutoLinkStatusEnum)[keyof typeof MillsheetAutoLinkStatusEnum]
/** ミルシートCSV取込モード（差分反映 or 上書き反映） */
export const MillsheetCsvImportModeEnum = {
  Differential: 'DIFFERENTIAL',
  Overwrite: 'OVERWRITE',
} as const

export type MillsheetCsvImportModeEnum = (typeof MillsheetCsvImportModeEnum)[keyof typeof MillsheetCsvImportModeEnum]
export type MillsheetDateRangeInput = {
  gte?: string | null | undefined
  lte?: string | null | undefined
}

export type MillsheetDuplicateGroupsInput = {
  page?: number | null | undefined
  pageSize?: number | null | undefined
  /** true = 未確認のグループだけ返す */
  unconfirmedOnly: boolean
}

export type MillsheetItemUpdateInput = {
  buildingPart?: string | null | undefined
  constructionArea?: string | null | undefined
  endorsementCompany?: string | null | undefined
  endorsementDate?: string | null | undefined
  id: string | number
  member?: string | null | undefined
  thicknessDiameter?: string | null | undefined
}

export type MillsheetNumberRangeInput = {
  gte?: number | null | undefined
  lte?: number | null | undefined
}

export type MillsheetSortConfigInput = {
  manufacturerName?: Array<string> | null | undefined
  sectionType?: Array<string> | null | undefined
  steelType?: Array<string> | null | undefined
}

export type MillsheetSortInput = {
  key: MillsheetSortKeyEnum
  order: SortOrderEnum
}

export const MillsheetSortKeyEnum = {
  BuildingPart: 'buildingPart',
  ConstructionArea: 'constructionArea',
  ConstructionName: 'constructionName',
  Customer: 'customer',
  InnerNo: 'innerNo',
  IssuedDate: 'issuedDate',
  ManufacturerName: 'manufacturerName',
  Member: 'member',
  No: 'no',
  OrderNumber: 'orderNumber',
  SectionTypes: 'sectionTypes',
  Sizes: 'sizes',
  SteelOrder: 'steelOrder',
  SteelTypes: 'steelTypes',
  SubmissionDate: 'submissionDate',
  TotalWeight: 'totalWeight',
  TradingCompany: 'tradingCompany',
} as const

export type MillsheetSortKeyEnum = (typeof MillsheetSortKeyEnum)[keyof typeof MillsheetSortKeyEnum]
export type MillsheetStringContainsInput = {
  contains?: string | null | undefined
}

export type MillsheetStringInInput = {
  in?: Array<string> | null | undefined
}

export type MillsheetUpdateInput = {
  /** 指定すると配下の全明細の部位をこの値で上書きする */
  buildingPart?: string | null | undefined
  /** 指定すると配下の全明細の工区をこの値で上書きする */
  constructionArea?: string | null | undefined
  id: string | number
  innerNo?: string | null | undefined
  /** 指定すると配下の全明細の節をこの値で上書きする */
  member?: string | null | undefined
  submissionDate?: string | null | undefined
}

export type MillsheetsBulkOperationTargetInput = {
  filter?: MillsheetsSearchInput | null | undefined
  ids?: Array<string | number> | null | undefined
}

export type MillsheetsFilterInput = {
  buildingPart?: MillsheetStringInInput | null | undefined
  constructionArea?: MillsheetStringInInput | null | undefined
  constructionName?: MillsheetStringInInput | null | undefined
  customer?: MillsheetStringInInput | null | undefined
  ids?: Array<string | number> | null | undefined
  innerNo?: MillsheetStringContainsInput | null | undefined
  issuedDate?: MillsheetDateRangeInput | null | undefined
  manufacturerName?: MillsheetStringInInput | null | undefined
  member?: MillsheetStringInInput | null | undefined
  no?: MillsheetStringContainsInput | null | undefined
  orderNumber?: MillsheetStringContainsInput | null | undefined
  sectionTypes?: MillsheetStringContainsInput | null | undefined
  steelEndorsementCompany?: MillsheetStringInInput | null | undefined
  steelEndorsementDate?: MillsheetDateRangeInput | null | undefined
  steelHeatNumber?: MillsheetStringContainsInput | null | undefined
  steelIndividualNumber?: MillsheetStringContainsInput | null | undefined
  steelLength?: MillsheetStringContainsInput | null | undefined
  steelSectionType?: MillsheetStringInInput | null | undefined
  steelSize?: MillsheetStringContainsInput | null | undefined
  steelSteelQuantity?: MillsheetNumberRangeInput | null | undefined
  steelSteelType?: MillsheetStringInInput | null | undefined
  steelTypes?: MillsheetStringContainsInput | null | undefined
  steelWeight?: MillsheetNumberRangeInput | null | undefined
  submissionDate?: MillsheetStringInInput | null | undefined
  totalWeight?: MillsheetNumberRangeInput | null | undefined
  tradingCompany?: MillsheetStringInInput | null | undefined
}

export type MillsheetsSearchInput = {
  filter?: MillsheetsFilterInput | null | undefined
  freeWord?: string | null | undefined
  page?: number | null | undefined
  pageSize?: number | null | undefined
  sorts?: Array<MillsheetSortInput> | null | undefined
}

export type MillsheetsUpdateInput = {
  /** 指定すると対象ミルシート配下の全明細の部位をこの値で上書きする */
  buildingPart?: string | null | undefined
  /** 指定すると対象ミルシート配下の全明細の工区をこの値で上書きする */
  constructionArea?: string | null | undefined
  ids?: Array<string | number> | null | undefined
  innerNo?: string | null | undefined
  /** 指定すると対象ミルシート配下の全明細の節をこの値で上書きする */
  member?: string | null | undefined
  submissionDate?: string | null | undefined
  target?: MillsheetsBulkOperationTargetInput | null | undefined
}

export const OrderLineMatchingArrivalStatus = {
  Completed: 'completed',
  NotArrived: 'notArrived',
  PartiallyArrived: 'partiallyArrived',
  Surplus: 'surplus',
} as const

export type OrderLineMatchingArrivalStatus =
  (typeof OrderLineMatchingArrivalStatus)[keyof typeof OrderLineMatchingArrivalStatus]
export const OrderLineMatchingConfidence = {
  Aggregated: 'aggregated',
  Confirmed: 'confirmed',
  Estimated: 'estimated',
  Manual: 'manual',
} as const

export type OrderLineMatchingConfidence = (typeof OrderLineMatchingConfidence)[keyof typeof OrderLineMatchingConfidence]
export const OrderLineMatchingEditableField = {
  Length: 'length',
  PlateNumber: 'plateNumber',
  Quantity: 'quantity',
  SectionType: 'sectionType',
  Size: 'size',
  SteelType: 'steelType',
  Weight: 'weight',
} as const

export type OrderLineMatchingEditableField =
  (typeof OrderLineMatchingEditableField)[keyof typeof OrderLineMatchingEditableField]
export type OrderLineMatchingFiltersInput = {
  reasons?: Array<OrderLineMatchingReviewReason> | null | undefined
  review?: OrderLineMatchingReviewFilter | null | undefined
  search?: string | null | undefined
  sections?: Array<string> | null | undefined
  statuses?: Array<OrderLineMatchingArrivalStatus> | null | undefined
  suppliers?: Array<string> | null | undefined
  weightDiffMax?: number | null | undefined
  weightDiffMin?: number | null | undefined
  weightDiffRateAbsMax?: number | null | undefined
  weightDiffRateAbsMin?: number | null | undefined
}

export const OrderLineMatchingMethod = {
  CompositeKey: 'compositeKey',
  LengthConversion: 'lengthConversion',
  Manual: 'manual',
  Number: 'number',
  WeightEstimate: 'weightEstimate',
} as const

export type OrderLineMatchingMethod = (typeof OrderLineMatchingMethod)[keyof typeof OrderLineMatchingMethod]
export type OrderLineMatchingProjectInput = {
  filters?: OrderLineMatchingFiltersInput | null | undefined
  page?: number | null | undefined
  pageSize?: number | null | undefined
  projectKey: string
  tenantId?: string | number | null | undefined
}

export type OrderLineMatchingProjectSortInput = {
  key: OrderLineMatchingProjectSortKeyEnum
  order: SortOrderEnum
}

export const OrderLineMatchingProjectSortKeyEnum = {
  AllocatedWeight: 'allocatedWeight',
  OrderCount: 'orderCount',
  Progress: 'progress',
  ProjectName: 'projectName',
  ReviewItemCount: 'reviewItemCount',
  TotalWeight: 'totalWeight',
  UpdatedAt: 'updatedAt',
} as const

export type OrderLineMatchingProjectSortKeyEnum =
  (typeof OrderLineMatchingProjectSortKeyEnum)[keyof typeof OrderLineMatchingProjectSortKeyEnum]
export type OrderLineMatchingProjectsInput = {
  page?: number | null | undefined
  pageSize?: number | null | undefined
  search?: string | null | undefined
  sorts?: Array<OrderLineMatchingProjectSortInput> | null | undefined
  tenantId?: string | number | null | undefined
}

export const OrderLineMatchingReviewFilter = {
  All: 'all',
  None: 'none',
  Required: 'required',
} as const

export type OrderLineMatchingReviewFilter =
  (typeof OrderLineMatchingReviewFilter)[keyof typeof OrderLineMatchingReviewFilter]
export const OrderLineMatchingReviewReason = {
  DuplicateSuspected: 'duplicateSuspected',
  LowConfidence: 'lowConfidence',
  MultipleCandidates: 'multipleCandidates',
  ProjectMismatch: 'projectMismatch',
  SupplierMismatch: 'supplierMismatch',
} as const

export type OrderLineMatchingReviewReason =
  (typeof OrderLineMatchingReviewReason)[keyof typeof OrderLineMatchingReviewReason]
export type OrderLineMatchingUnlinkedInput = {
  filter?: MillsheetsFilterInput | null | undefined
  freeWord?: string | null | undefined
  page?: number | null | undefined
  pageSize?: number | null | undefined
}

export type OrderPdfMappingInput = {
  s3ObjectId: string
  sourceFileName: string
}

export const OrderStatusEnum = {
  Extracting: 'extracting',
  ReadFailed: 'readFailed',
  Registered: 'registered',
  Rejected: 'rejected',
} as const

export type OrderStatusEnum = (typeof OrderStatusEnum)[keyof typeof OrderStatusEnum]
export type PagedAdminIdentitiesFilterInput = {
  email?: string | null | undefined
  includeArchived?: boolean | null | undefined
  roles?: Array<IdentityRoleEnum | null | undefined> | null | undefined
}

export type PagedAdminIdentitiesInput = {
  filter?: PagedAdminIdentitiesFilterInput | null | undefined
  page?: number | null | undefined
  pageSize?: number | null | undefined
}

export type PlanCreateInput = {
  name: string
  serviceTerms: string
}

export type PlanUpdateInput = {
  id: string | number
  name: string
  serviceTerms: string
}

export type ReleaseOrderLineMatchingAllocationInput = {
  millsheetItemId: string | number
  orderItemId: string | number
}

export type RestoreAdminIdentityInput = {
  id: string | number
}

export type S3ObjectCreateInput = {
  contentLength: number
  contentType: string
  originalFileName: string
  resourceType: S3ObjectResourceTypeEnum
}

export const S3ObjectResourceTypeEnum = {
  MillsheetAttachments: 'millsheetAttachments',
  Millsheets: 'millsheets',
  NestingDiagrams: 'nestingDiagrams',
  OrderPdfs: 'orderPdfs',
  ShearingCertificates: 'shearingCertificates',
} as const

export type S3ObjectResourceTypeEnum = (typeof S3ObjectResourceTypeEnum)[keyof typeof S3ObjectResourceTypeEnum]
export type S3ObjectUpdateInput = {
  id: string | number
  millsheetId?: string | null | undefined
}

/** 変換マスタ（表示用の名寄せ）の変換対象フィールド */
export const ShapeNameConversionFieldEnum = {
  Length: 'length',
  ProductName: 'productName',
  Size: 'size',
} as const

export type ShapeNameConversionFieldEnum =
  (typeof ShapeNameConversionFieldEnum)[keyof typeof ShapeNameConversionFieldEnum]
export type ShearingCertificateDateRangeInput = {
  gte?: string | null | undefined
  lte?: string | null | undefined
}

export type ShearingCertificateSortInput = {
  key: ShearingCertificateSortKeyEnum
  order: SortOrderEnum
}

export const ShearingCertificateSortKeyEnum = {
  CertificateNo: 'certificateNo',
  ConstructionName: 'constructionName',
  FileUrl: 'fileUrl',
  IssuedDate: 'issuedDate',
  IssuingCompanyName: 'issuingCompanyName',
} as const

export type ShearingCertificateSortKeyEnum =
  (typeof ShearingCertificateSortKeyEnum)[keyof typeof ShearingCertificateSortKeyEnum]
export type ShearingCertificateStringContainsInput = {
  contains?: string | null | undefined
}

export type ShearingCertificateStringInInput = {
  in?: Array<string> | null | undefined
}

export type ShearingCertificatesFilterInput = {
  certificateNo?: ShearingCertificateStringContainsInput | null | undefined
  constructionName?: ShearingCertificateStringInInput | null | undefined
  ids?: Array<string | number> | null | undefined
  issuedDate?: ShearingCertificateDateRangeInput | null | undefined
  issuingCompanyName?: ShearingCertificateStringInInput | null | undefined
  itemIndividualNumber?: ShearingCertificateStringContainsInput | null | undefined
  itemManufacturerName?: ShearingCertificateStringInInput | null | undefined
  itemNo?: ShearingCertificateStringContainsInput | null | undefined
  itemSteelType?: ShearingCertificateStringInInput | null | undefined
  itemThickness?: ShearingCertificateStringContainsInput | null | undefined
}

export type ShearingCertificatesSearchInput = {
  filter?: ShearingCertificatesFilterInput | null | undefined
  freeWord?: string | null | undefined
  page?: number | null | undefined
  pageSize?: number | null | undefined
  sorts?: Array<ShearingCertificateSortInput> | null | undefined
}

/** ソート方向（Prisma の SortOrder と同じ値） */
export const SortOrderEnum = {
  Asc: 'asc',
  Desc: 'desc',
} as const

export type SortOrderEnum = (typeof SortOrderEnum)[keyof typeof SortOrderEnum]
/** 発注の分類（ロール品 / スポット品） */
export const SteelOrderCategoryEnum = {
  Roll: 'roll',
  Spot: 'spot',
} as const

export type SteelOrderCategoryEnum = (typeof SteelOrderCategoryEnum)[keyof typeof SteelOrderCategoryEnum]
/** 発注データ CSV のファイル種別 */
export const SteelOrderCsvImportKindEnum = {
  DeliveryTable: 'deliveryTable',
  Scrm: 'scrm',
} as const

export type SteelOrderCsvImportKindEnum = (typeof SteelOrderCsvImportKindEnum)[keyof typeof SteelOrderCsvImportKindEnum]
/** 発注データ CSV の取込経路 */
export const SteelOrderCsvImportSourceEnum = {
  Manual: 'manual',
  S3: 's3',
} as const

export type SteelOrderCsvImportSourceEnum =
  (typeof SteelOrderCsvImportSourceEnum)[keyof typeof SteelOrderCsvImportSourceEnum]
/** 発注データ CSV 取込の結果 */
export const SteelOrderCsvImportStatusEnum = {
  Failed: 'failed',
  PartiallyFailed: 'partiallyFailed',
  Succeeded: 'succeeded',
} as const

export type SteelOrderCsvImportStatusEnum =
  (typeof SteelOrderCsvImportStatusEnum)[keyof typeof SteelOrderCsvImportStatusEnum]
/** ミルシートステータス / 発注ステータス共通の照合結果（「対象外」はフラグで表現する） */
export const SteelOrderMatchingStatusEnum = {
  Completed: 'completed',
  Incomplete: 'incomplete',
  Unregistered: 'unregistered',
} as const

export type SteelOrderMatchingStatusEnum =
  (typeof SteelOrderMatchingStatusEnum)[keyof typeof SteelOrderMatchingStatusEnum]
/** 品管照合ステータス（品質管理部門の手動ダブルチェック記録） */
export const SteelOrderQualityCheckStatusEnum = {
  Completed: 'completed',
  Incomplete: 'incomplete',
  Unregistered: 'unregistered',
} as const

export type SteelOrderQualityCheckStatusEnum =
  (typeof SteelOrderQualityCheckStatusEnum)[keyof typeof SteelOrderQualityCheckStatusEnum]
export type SteelOrderSortInput = {
  key: SteelOrderSortKeyEnum
  order: SortOrderEnum
}

/** 発注一覧のソートキー */
export const SteelOrderSortKeyEnum = {
  Category: 'category',
  ImportedAt: 'importedAt',
  MillsheetStatus: 'millsheetStatus',
  MillsheetWeight: 'millsheetWeight',
  OrderStatus: 'orderStatus',
  OrderedWeight: 'orderedWeight',
  OutputDate: 'outputDate',
  QualityCheckStatus: 'qualityCheckStatus',
  ReferenceNumber: 'referenceNumber',
  SupplierName: 'supplierName',
  TotalWeight: 'totalWeight',
} as const

export type SteelOrderSortKeyEnum = (typeof SteelOrderSortKeyEnum)[keyof typeof SteelOrderSortKeyEnum]
export type SteelOrdersBulkOperationTargetInput = {
  filter?: SteelOrdersSearchInput | null | undefined
  ids?: Array<string | number> | null | undefined
}

export type SteelOrdersSearchInput = {
  category?: SteelOrderCategoryEnum | null | undefined
  constructionName?: string | null | undefined
  constructionNumber?: string | null | undefined
  freeWord?: string | null | undefined
  ids?: Array<string | number> | null | undefined
  importedAtFrom?: string | null | undefined
  importedAtTo?: string | null | undefined
  isMillsheetExcluded?: boolean | null | undefined
  managementNumber?: string | null | undefined
  manufacturerName?: string | null | undefined
  millsheetStatus?: SteelOrderMatchingStatusEnum | null | undefined
  orderStatus?: SteelOrderMatchingStatusEnum | null | undefined
  outputDateFrom?: string | null | undefined
  outputDateTo?: string | null | undefined
  page?: number | null | undefined
  pageSize?: number | null | undefined
  productName?: string | null | undefined
  qualityCheckStatus?: SteelOrderQualityCheckStatusEnum | null | undefined
  referenceNumber?: string | null | undefined
  sortKey?: SteelOrderSortKeyEnum | null | undefined
  sortOrder?: SortOrderEnum | null | undefined
  sorts?: Array<SteelOrderSortInput> | null | undefined
  standard?: string | null | undefined
  supplierName?: string | null | undefined
}

export type SteelOrdersUpdateInput = {
  ids?: Array<string | number> | null | undefined
  isMillsheetExcluded?: boolean | null | undefined
  outputDate?: string | null | undefined
  target?: SteelOrdersBulkOperationTargetInput | null | undefined
}

export type TenantSettingUpdateInput = {
  millsheetSortConfig?: MillsheetSortConfigInput | null | undefined
}

/** テナントの状態を表す列挙型 */
export const TenantStatusEnum = {
  Active: 'active',
  PreActive: 'preActive',
  Suspended: 'suspended',
  Trial: 'trial',
} as const

export type TenantStatusEnum = (typeof TenantStatusEnum)[keyof typeof TenantStatusEnum]
export type UpdateAdminIdentityRoleInput = {
  id: string | number
  role: IdentityRoleEnum
}

export type UpdateOrderLineMatchingItemInput = {
  field: OrderLineMatchingEditableField
  itemId: string | number
  rawValue: string
}

export type UpsertOrderLineMatchingAllocationInput = {
  millsheetItemId: string | number
  orderItemId: string | number
  originalQuantity?: number | null | undefined
  quantity?: number | null | undefined
  weight: number
}

export type UserInput = {
  id: string | number
  name: string
  role?: UserRoleEnum | null | undefined
  smsMfaRequired?: boolean | null | undefined
}

export const UserRoleEnum = {
  Admin: 'admin',
  Manager: 'manager',
  Member: 'member',
} as const

export type UserRoleEnum = (typeof UserRoleEnum)[keyof typeof UserRoleEnum]
export type AdminIdentityFieldsFragment = {
  id: string
  email: string
  role: IdentityRoleEnum
  createdAt: string
  archivedAt: string | null
}

export type CreateMillsheetsByCsvResultFieldsFragment = {
  index: number
  success: boolean
  isEmptyLine: boolean | null
  message: string | null
  resultTitle: string | null
  isDuplicate: boolean | null
}

export type CreateShearingCertificatesByCsvResultFieldsFragment = {
  index: number
  success: boolean
  isEmptyLine: boolean | null
  message: string | null
  resultTitle: string | null
  isDuplicate: boolean | null
}

export type MeFieldsFragment = {
  id: string
  name: string
  email: string
  imageUrl: string | null
  role: UserRoleEnum
  tenant: {
    id: string
    name: string
    companyName: string
    prefecture: string
    memo: string
    status: TenantStatusEnum
    createdAt: string
    setting: {
      id: string
      orderLineMatchingEnabled: boolean
      orderMatchingEnabled: boolean
      scrmSiteCode: string | null
      millsheetSortConfig: {
        manufacturerName: Array<string> | null
        steelType: Array<string> | null
        sectionType: Array<string> | null
      } | null
    } | null
  }
}

export type MillsheetDuplicateGroupFieldsFragment = {
  key: string
  confirmed: boolean
  millsheet: {
    id: string
    no: string
    constructionName: string
    orderNumber: string
    s3Objects: Array<{ id: string; originalFileName: string }>
  }
  items: Array<{
    id: string
    heatNumber: string
    individualNumber: string
    pageNumber: number | null
    steelType: string
    sectionType: string
    constructionArea: string
    member: string
    buildingPart: string
    quantity: number
    size: string
    thicknessDiameter: string | null
    endorsementCompany: string | null
    endorsementDate: string | null
    endorsementCompany2: string | null
    endorsementDate2: string | null
    endorsementCompany3: string | null
    endorsementDate3: string | null
    endorsementCompany4: string | null
    endorsementDate4: string | null
    endorsementCompany5: string | null
    endorsementDate5: string | null
    endorsementCompany6: string | null
    endorsementDate6: string | null
    weightBeforeProcessing: number | null
    length: string
    rank: number
  }>
}

export type MillsheetFieldsFragment = {
  id: string
  tenantId: string
  no: string
  manufacturerName: string
  issuedDate: string
  submissionDate: string | null
  tradingCompany: string
  customer: string
  constructionArea: string
  member: string
  buildingPart: string
  constructionName: string
  orderNumber: string
  innerNo: string
  steelOrderId: string | null
  millsheetItems: Array<{
    id: string
    heatNumber: string
    individualNumber: string
    pageNumber: number | null
    steelType: string
    sectionType: string
    constructionArea: string
    member: string
    buildingPart: string
    quantity: number
    size: string
    thicknessDiameter: string | null
    endorsementCompany: string | null
    endorsementDate: string | null
    endorsementCompany2: string | null
    endorsementDate2: string | null
    endorsementCompany3: string | null
    endorsementDate3: string | null
    endorsementCompany4: string | null
    endorsementDate4: string | null
    endorsementCompany5: string | null
    endorsementDate5: string | null
    endorsementCompany6: string | null
    endorsementDate6: string | null
    weightBeforeProcessing: number | null
    length: string
    rank: number
  }>
  s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum; thumbnailCount: number | null }>
  shearingCertificates: Array<{ id: string; certificateNo: string | null; s3Object: { id: string } | null }>
}

export type MillsheetItemFieldsFragment = {
  id: string
  heatNumber: string
  individualNumber: string
  pageNumber: number | null
  steelType: string
  sectionType: string
  constructionArea: string
  member: string
  buildingPart: string
  quantity: number
  size: string
  thicknessDiameter: string | null
  endorsementCompany: string | null
  endorsementDate: string | null
  endorsementCompany2: string | null
  endorsementDate2: string | null
  endorsementCompany3: string | null
  endorsementDate3: string | null
  endorsementCompany4: string | null
  endorsementDate4: string | null
  endorsementCompany5: string | null
  endorsementDate5: string | null
  endorsementCompany6: string | null
  endorsementDate6: string | null
  weightBeforeProcessing: number | null
  length: string
  rank: number
}

export type MyIdentityFieldsFragment = {
  id: string
  email: string
  role: IdentityRoleEnum
  users: Array<{
    id: string
    name: string
    email: string
    imageUrl: string | null
    role: UserRoleEnum
    tenant: {
      id: string
      name: string
      companyName: string
      prefecture: string
      memo: string
      status: TenantStatusEnum
      createdAt: string
      setting: {
        id: string
        orderLineMatchingEnabled: boolean
        orderMatchingEnabled: boolean
        scrmSiteCode: string | null
        millsheetSortConfig: {
          manufacturerName: Array<string> | null
          steelType: Array<string> | null
          sectionType: Array<string> | null
        } | null
      } | null
    }
  }>
}

export type MyTenantFieldsFragment = {
  id: string
  name: string
  companyName: string
  prefecture: string
  memo: string
  status: TenantStatusEnum
  createdAt: string
  setting: {
    id: string
    orderLineMatchingEnabled: boolean
    orderMatchingEnabled: boolean
    scrmSiteCode: string | null
    millsheetSortConfig: {
      manufacturerName: Array<string> | null
      steelType: Array<string> | null
      sectionType: Array<string> | null
    } | null
  } | null
}

export type NestingDiagramFieldsFragment = {
  id: string
  individualNumber: string
  shearingCertificateItemId: string | null
  s3Object: { id: string } | null
}

export type NestingDiagramManageFieldsFragment = {
  id: string
  individualNumber: string
  shearingCertificateItemId: string | null
  linkedCertificateNo: string | null
  createdAt: string
  s3Object: { id: string } | null
}

export type OrderDetailFieldsFragment = {
  extraction: unknown
  id: string
  tenantId: string
  sourceFileName: string
  projectName: string
  projectNameFixed: boolean
  supplierName: string | null
  orderNumber: string | null
  orderedAt: string | null
  deliveryDueAt: string | null
  deliveryPlace: string | null
  remarks: string | null
  s3ObjectId: string
  status: OrderStatusEnum
  note: string | null
  uploadedAt: string
  registeredAt: string | null
  extractedAt: string | null
  extractionModel: string | null
  extractionError: string | null
  updatedAt: string
  ocrRetryAvailableAt: string | null
  confirmedAt: string | null
  confirmedByName: string | null
  itemCount: number
  items: Array<{
    id: string
    lineNo: number
    steelType: string
    sectionType: string
    size: string
    length: number | null
    quantity: number | null
    weight: number | null
    plateNumber: string | null
    buildingPart: string | null
    constructionArea: string | null
    editedAt: string | null
  }>
}

export type OrderFieldsFragment = {
  id: string
  tenantId: string
  sourceFileName: string
  projectName: string
  projectNameFixed: boolean
  supplierName: string | null
  orderNumber: string | null
  orderedAt: string | null
  deliveryDueAt: string | null
  deliveryPlace: string | null
  remarks: string | null
  s3ObjectId: string
  status: OrderStatusEnum
  note: string | null
  uploadedAt: string
  registeredAt: string | null
  extractedAt: string | null
  extractionModel: string | null
  extractionError: string | null
  updatedAt: string
  ocrRetryAvailableAt: string | null
  confirmedAt: string | null
  confirmedByName: string | null
  itemCount: number
}

export type PlanFieldsFragment = { id: string; name: string; serviceTerms: string }

export type ShapeNameConversionFieldsFragment = {
  id: string
  tenantId: string
  field: ShapeNameConversionFieldEnum
  sourceValue: string
  normalizedValue: string
  createdAt: string
  updatedAt: string
}

export type ShearingCertificateFieldsFragment = {
  id: string
  tenantId: string
  issuingCompanyName: string
  issuedDate: string
  certificateNo: string | null
  constructionName: string
  items: Array<{
    id: string
    steelType: string
    thickness: string
    weight: number | null
    no: string
    individualNumber: string
    manufacturerName: string
    rank: number
    millsheets: Array<{ id: string; s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum }> }>
    nestingDiagrams: Array<{
      id: string
      individualNumber: string
      shearingCertificateItemId: string | null
      s3Object: { id: string } | null
    }>
  }>
  s3Object: { id: string } | null
}

export type ShearingCertificateItemFieldsFragment = {
  id: string
  steelType: string
  thickness: string
  weight: number | null
  no: string
  individualNumber: string
  manufacturerName: string
  rank: number
  millsheets: Array<{ id: string; s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum }> }>
  nestingDiagrams: Array<{
    id: string
    individualNumber: string
    shearingCertificateItemId: string | null
    s3Object: { id: string } | null
  }>
}

export type ShearingCertificatePdfFieldsFragment = {
  id: string
  originalFileName: string
  createdAt: string
  linkedShearingCertificateId: string | null
  linkedShearingCertificateNo: string | null
}

export type SteelOrderCsvImportFieldsFragment = {
  id: string
  tenantId: string
  kind: SteelOrderCsvImportKindEnum
  source: SteelOrderCsvImportSourceEnum
  fileName: string
  status: SteelOrderCsvImportStatusEnum
  createdByName: string | null
  createdAt: string
  summary: {
    totalRowCount: number
    emptyRowCount: number
    importedRowCount: number
    skippedContractNumbers: Array<string>
    errorRows: Array<{ rowNumber: number; message: string }>
    skippedSiteCodeRows: Array<{ siteCode: string; rowCount: number }>
  }
}

export type SteelOrderCsvImportRowResultFieldsFragment = {
  index: number
  success: boolean
  isEmptyLine: boolean | null
  message: string | null
  resultTitle: string | null
  skippedByContractNumberError: boolean | null
  skippedByUnknownSiteCode: boolean | null
}

export type SteelOrderDetailFieldsFragment = {
  id: string
  tenantId: string
  referenceNumber: string
  category: SteelOrderCategoryEnum
  millsheetStatus: SteelOrderMatchingStatusEnum
  isMillsheetExcluded: boolean
  orderStatus: SteelOrderMatchingStatusEnum
  qualityCheckStatus: SteelOrderQualityCheckStatusEnum
  outputDate: string | null
  supplierName: string | null
  importedAt: string
  totalWeight: number
  millsheetWeight: number
  orderedWeight: number
  millsheets: Array<{
    id: string
    no: string
    orderNumber: string
    constructionName: string
    tenantId: string
    manufacturerName: string
    issuedDate: string
    submissionDate: string | null
    tradingCompany: string
    customer: string
    constructionArea: string
    member: string
    buildingPart: string
    innerNo: string
    steelOrderId: string | null
    s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum; thumbnailCount: number | null }>
    millsheetItems: Array<{
      id: string
      heatNumber: string
      individualNumber: string
      pageNumber: number | null
      steelType: string
      sectionType: string
      constructionArea: string
      member: string
      buildingPart: string
      quantity: number
      size: string
      thicknessDiameter: string | null
      endorsementCompany: string | null
      endorsementDate: string | null
      endorsementCompany2: string | null
      endorsementDate2: string | null
      endorsementCompany3: string | null
      endorsementDate3: string | null
      endorsementCompany4: string | null
      endorsementDate4: string | null
      endorsementCompany5: string | null
      endorsementDate5: string | null
      endorsementCompany6: string | null
      endorsementDate6: string | null
      weightBeforeProcessing: number | null
      length: string
      rank: number
    }>
    shearingCertificates: Array<{ id: string; certificateNo: string | null; s3Object: { id: string } | null }>
  }>
  items: Array<{
    id: string
    tenantId: string
    steelOrderId: string
    managementNumber: string | null
    constructionNumber: string | null
    constructionName: string | null
    productName: string | null
    standard: string | null
    size: string | null
    length: string | null
    quantity: number | null
    weight: number | null
    manufacturerName: string | null
    itemNumber: string | null
    answeredDeliveryDate: string | null
    constructionNote: string | null
    qualityCheckStatus: SteelOrderQualityCheckStatusEnum
    qualityCheckUpdatedAt: string | null
    rank: number
    createdAt: string
  }>
}

export type SteelOrderFieldsFragment = {
  id: string
  tenantId: string
  referenceNumber: string
  category: SteelOrderCategoryEnum
  millsheetStatus: SteelOrderMatchingStatusEnum
  isMillsheetExcluded: boolean
  orderStatus: SteelOrderMatchingStatusEnum
  qualityCheckStatus: SteelOrderQualityCheckStatusEnum
  outputDate: string | null
  supplierName: string | null
  importedAt: string
  totalWeight: number
  millsheetWeight: number
  orderedWeight: number
  items: Array<{
    id: string
    tenantId: string
    steelOrderId: string
    managementNumber: string | null
    constructionNumber: string | null
    constructionName: string | null
    productName: string | null
    standard: string | null
    size: string | null
    length: string | null
    quantity: number | null
    weight: number | null
    manufacturerName: string | null
    itemNumber: string | null
    answeredDeliveryDate: string | null
    constructionNote: string | null
    qualityCheckStatus: SteelOrderQualityCheckStatusEnum
    qualityCheckUpdatedAt: string | null
    rank: number
    createdAt: string
  }>
  millsheets: Array<{
    id: string
    no: string
    orderNumber: string
    constructionName: string
    s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum }>
  }>
}

export type SteelOrderItemFieldsFragment = {
  id: string
  tenantId: string
  steelOrderId: string
  managementNumber: string | null
  constructionNumber: string | null
  constructionName: string | null
  productName: string | null
  standard: string | null
  size: string | null
  length: string | null
  quantity: number | null
  weight: number | null
  manufacturerName: string | null
  itemNumber: string | null
  answeredDeliveryDate: string | null
  constructionNote: string | null
  qualityCheckStatus: SteelOrderQualityCheckStatusEnum
  qualityCheckUpdatedAt: string | null
  rank: number
  createdAt: string
}

export type TenantFieldsFragment = {
  id: string
  name: string
  companyName: string
  prefecture: string
  memo: string
  status: TenantStatusEnum
  introductionDate: string | null
  lastConversationDate: string | null
  lastInterviewDate: string | null
  createdAt: string
  setting: {
    id: string
    orderLineMatchingEnabled: boolean
    orderMatchingEnabled: boolean
    scrmSiteCode: string | null
    millsheetSortConfig: {
      manufacturerName: Array<string> | null
      steelType: Array<string> | null
      sectionType: Array<string> | null
    } | null
  } | null
  users: Array<{
    id: string
    name: string
    email: string
    role: UserRoleEnum
    smsMfaRequired: boolean
    createdAt: string
    signedUpAt: string | null
  }>
  tenantPlan: {
    id: string
    planId: string
    quoteNumber: string
    plan: { id: string; name: string; serviceTerms: string }
  } | null
}

export type TenantSimpleFieldsFragment = {
  id: string
  name: string
  companyName: string
  prefecture: string
  status: TenantStatusEnum
  memo: string
  introductionDate: string | null
  lastConversationDate: string | null
  lastInterviewDate: string | null
  createdAt: string
  tenantPlan: {
    id: string
    planId: string
    quoteNumber: string
    plan: { id: string; name: string; serviceTerms: string }
  } | null
}

export type TenantSettingFieldsFragment = {
  id: string
  orderLineMatchingEnabled: boolean
  orderMatchingEnabled: boolean
  scrmSiteCode: string | null
  millsheetSortConfig: {
    manufacturerName: Array<string> | null
    steelType: Array<string> | null
    sectionType: Array<string> | null
  } | null
}

export type UserFieldsFragment = {
  id: string
  name: string
  email: string
  role: UserRoleEnum
  smsMfaRequired: boolean
  createdAt: string
  signedUpAt: string | null
}

export type CreateInvitationMutationVariables = Exact<{
  input: CreateInvitationInput
}>

export type CreateInvitationMutation = {
  createInvitation: {
    id: string
    name: string
    email: string
    role: UserRoleEnum
    smsMfaRequired: boolean
    createdAt: string
    signedUpAt: string | null
  }
}

export type AgreeToServiceTermsMutationVariables = Exact<{ [key: string]: never }>

export type AgreeToServiceTermsMutation = {
  agreeToServiceTerms: {
    id: string
    name: string
    email: string
    imageUrl: string | null
    role: UserRoleEnum
    tenant: {
      id: string
      name: string
      companyName: string
      prefecture: string
      memo: string
      status: TenantStatusEnum
      createdAt: string
      setting: {
        id: string
        orderLineMatchingEnabled: boolean
        orderMatchingEnabled: boolean
        scrmSiteCode: string | null
        millsheetSortConfig: {
          manufacturerName: Array<string> | null
          steelType: Array<string> | null
          sectionType: Array<string> | null
        } | null
      } | null
    }
  }
}

export type ArchiveAdminIdentityMutationVariables = Exact<{
  input: ArchiveAdminIdentityInput
}>

export type ArchiveAdminIdentityMutation = {
  archiveAdminIdentity: {
    id: string
    email: string
    role: IdentityRoleEnum
    createdAt: string
    archivedAt: string | null
  }
}

export type ConfirmMillsheetDuplicateGroupMutationVariables = Exact<{
  millsheetId: string | number
  itemIds: Array<string | number> | string | number
}>

export type ConfirmMillsheetDuplicateGroupMutation = { confirmMillsheetDuplicateGroup: number }

export type ConfirmOrderMutationVariables = Exact<{
  id: string
}>

export type ConfirmOrderMutation = {
  confirmOrder: {
    id: string
    tenantId: string
    sourceFileName: string
    projectName: string
    projectNameFixed: boolean
    supplierName: string | null
    orderNumber: string | null
    orderedAt: string | null
    deliveryDueAt: string | null
    deliveryPlace: string | null
    remarks: string | null
    s3ObjectId: string
    status: OrderStatusEnum
    note: string | null
    uploadedAt: string
    registeredAt: string | null
    extractedAt: string | null
    extractionModel: string | null
    extractionError: string | null
    updatedAt: string
    ocrRetryAvailableAt: string | null
    confirmedAt: string | null
    confirmedByName: string | null
    itemCount: number
  }
}

export type CreateAdminIdentityInvitationMutationVariables = Exact<{
  input: CreateAdminIdentityInvitationInput
}>

export type CreateAdminIdentityInvitationMutation = {
  createAdminIdentityInvitation: {
    id: string
    email: string
    role: IdentityRoleEnum
    createdAt: string
    archivedAt: string | null
  }
}

export type CreateAdminInvitationMutationVariables = Exact<{
  input: CreateAdminInvitationInput
}>

export type CreateAdminInvitationMutation = {
  createAdminInvitation: {
    id: string
    name: string
    email: string
    role: UserRoleEnum
    smsMfaRequired: boolean
    createdAt: string
    signedUpAt: string | null
  }
}

export type CreateAdminPlanMutationVariables = Exact<{
  plan: PlanCreateInput
}>

export type CreateAdminPlanMutation = { createAdminPlan: { id: string; name: string; serviceTerms: string } | null }

export type CreateMillsheetsByCsvMutationVariables = Exact<{
  csv: string
  mode: MillsheetCsvImportModeEnum
}>

export type CreateMillsheetsByCsvMutation = {
  createMillsheetsByCsv: Array<{
    index: number
    success: boolean
    isEmptyLine: boolean | null
    message: string | null
    resultTitle: string | null
    isDuplicate: boolean | null
  }>
}

export type CreateOrderFromPdfMutationVariables = Exact<{
  s3ObjectId: string
  projectName?: string | null | undefined
}>

export type CreateOrderFromPdfMutation = {
  createOrderFromPdf: {
    id: string
    tenantId: string
    sourceFileName: string
    projectName: string
    projectNameFixed: boolean
    supplierName: string | null
    orderNumber: string | null
    orderedAt: string | null
    deliveryDueAt: string | null
    deliveryPlace: string | null
    remarks: string | null
    s3ObjectId: string
    status: OrderStatusEnum
    note: string | null
    uploadedAt: string
    registeredAt: string | null
    extractedAt: string | null
    extractionModel: string | null
    extractionError: string | null
    updatedAt: string
    ocrRetryAvailableAt: string | null
    confirmedAt: string | null
    confirmedByName: string | null
    itemCount: number
  }
}

export type CreateS3ObjectMutationVariables = Exact<{
  input: S3ObjectCreateInput
}>

export type CreateS3ObjectMutation = {
  createS3Object: {
    id: string
    fileUrl: string
    fileUrlForPut: string
    millsheetAutoLink: {
      status: MillsheetAutoLinkStatusEnum
      millsheetNo: string | null
      millsheetConstructionName: string | null
      millsheetOrderNumber: string | null
      candidateConstructionNames: Array<string>
      candidateOrderNumbers: Array<string>
    } | null
  } | null
}

export type CreateS3ObjectThumbnailsMutationVariables = Exact<{
  s3ObjectId: string | number
}>

export type CreateS3ObjectThumbnailsMutation = {
  createS3ObjectThumbnails: { id: string; fileUrl: string; thumbnailFileUrls: Array<string> } | null
}

export type CreateShearingCertificatesByCsvMutationVariables = Exact<{
  csv: string
  mode: MillsheetCsvImportModeEnum
}>

export type CreateShearingCertificatesByCsvMutation = {
  createShearingCertificatesByCsv: Array<{
    index: number
    success: boolean
    isEmptyLine: boolean | null
    message: string | null
    resultTitle: string | null
    isDuplicate: boolean | null
  }>
}

export type CreateTenantMutationVariables = Exact<{
  tenant: AdminTenantCreateInput
}>

export type CreateTenantMutation = {
  createAdminTenant: {
    id: string
    name: string
    companyName: string
    prefecture: string
    memo: string
    status: TenantStatusEnum
    introductionDate: string | null
    lastConversationDate: string | null
    lastInterviewDate: string | null
    createdAt: string
    setting: {
      id: string
      orderLineMatchingEnabled: boolean
      orderMatchingEnabled: boolean
      scrmSiteCode: string | null
      millsheetSortConfig: {
        manufacturerName: Array<string> | null
        steelType: Array<string> | null
        sectionType: Array<string> | null
      } | null
    } | null
    users: Array<{
      id: string
      name: string
      email: string
      role: UserRoleEnum
      smsMfaRequired: boolean
      createdAt: string
      signedUpAt: string | null
    }>
    tenantPlan: {
      id: string
      planId: string
      quoteNumber: string
      plan: { id: string; name: string; serviceTerms: string }
    } | null
  } | null
}

export type DeleteAdminPlanMutationVariables = Exact<{
  id: string | number
}>

export type DeleteAdminPlanMutation = { deleteAdminPlan: { id: string; name: string; serviceTerms: string } | null }

export type DeleteAdminShapeNameConversionMutationVariables = Exact<{
  tenantId: string
  id: string | number
}>

export type DeleteAdminShapeNameConversionMutation = {
  deleteAdminShapeNameConversion: {
    id: string
    tenantId: string
    field: ShapeNameConversionFieldEnum
    sourceValue: string
    normalizedValue: string
    createdAt: string
    updatedAt: string
  }
}

export type DeleteAdminUserMutationVariables = Exact<{
  id: string | number
}>

export type DeleteAdminUserMutation = { deleteAdminUser: { id: string } | null }

export type DeleteMillsheetMutationVariables = Exact<{
  id: string | number
}>

export type DeleteMillsheetMutation = {
  deleteMillsheet: {
    id: string
    tenantId: string
    no: string
    manufacturerName: string
    issuedDate: string
    submissionDate: string | null
    tradingCompany: string
    customer: string
    constructionArea: string
    member: string
    buildingPart: string
    constructionName: string
    orderNumber: string
    innerNo: string
    steelOrderId: string | null
    millsheetItems: Array<{
      id: string
      heatNumber: string
      individualNumber: string
      pageNumber: number | null
      steelType: string
      sectionType: string
      constructionArea: string
      member: string
      buildingPart: string
      quantity: number
      size: string
      thicknessDiameter: string | null
      endorsementCompany: string | null
      endorsementDate: string | null
      endorsementCompany2: string | null
      endorsementDate2: string | null
      endorsementCompany3: string | null
      endorsementDate3: string | null
      endorsementCompany4: string | null
      endorsementDate4: string | null
      endorsementCompany5: string | null
      endorsementDate5: string | null
      endorsementCompany6: string | null
      endorsementDate6: string | null
      weightBeforeProcessing: number | null
      length: string
      rank: number
    }>
    s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum; thumbnailCount: number | null }>
    shearingCertificates: Array<{ id: string; certificateNo: string | null; s3Object: { id: string } | null }>
  } | null
}

export type DeleteMillsheetItemsMutationVariables = Exact<{
  ids: Array<string | number> | string | number
}>

export type DeleteMillsheetItemsMutation = { deleteMillsheetItems: number }

export type DeleteNestingDiagramMutationVariables = Exact<{
  id: string | number
}>

export type DeleteNestingDiagramMutation = { deleteNestingDiagram: { id: string } | null }

export type DeleteOrderLineMatchingOrderMutationVariables = Exact<{
  input: DeleteOrderLineMatchingOrderInput
}>

export type DeleteOrderLineMatchingOrderMutation = { deleteOrderLineMatchingOrder: boolean }

export type DeleteS3ObjectMutationVariables = Exact<{
  id: string | number
}>

export type DeleteS3ObjectMutation = { deleteS3Object: { id: string } | null }

export type DeleteShearingCertificateMutationVariables = Exact<{
  id: string | number
}>

export type DeleteShearingCertificateMutation = { deleteShearingCertificate: { id: string } | null }

export type DeleteSteelOrdersMutationVariables = Exact<{
  ids: Array<string | number> | string | number
}>

export type DeleteSteelOrdersMutation = { deleteSteelOrders: number }

export type DeleteSteelOrdersByTargetMutationVariables = Exact<{
  target: SteelOrdersBulkOperationTargetInput
}>

export type DeleteSteelOrdersByTargetMutation = { deleteSteelOrders: number }

export type DeleteTenantMutationVariables = Exact<{
  id: string | number
}>

export type DeleteTenantMutation = {
  deleteAdminTenant: {
    id: string
    name: string
    companyName: string
    prefecture: string
    memo: string
    status: TenantStatusEnum
    introductionDate: string | null
    lastConversationDate: string | null
    lastInterviewDate: string | null
    createdAt: string
    setting: {
      id: string
      orderLineMatchingEnabled: boolean
      orderMatchingEnabled: boolean
      scrmSiteCode: string | null
      millsheetSortConfig: {
        manufacturerName: Array<string> | null
        steelType: Array<string> | null
        sectionType: Array<string> | null
      } | null
    } | null
    users: Array<{
      id: string
      name: string
      email: string
      role: UserRoleEnum
      smsMfaRequired: boolean
      createdAt: string
      signedUpAt: string | null
    }>
    tenantPlan: {
      id: string
      planId: string
      quoteNumber: string
      plan: { id: string; name: string; serviceTerms: string }
    } | null
  } | null
}

export type DeleteUserMutationVariables = Exact<{
  id: string | number
}>

export type DeleteUserMutation = { deleteUser: { id: string } | null }

export type DetachShearingCertificatePdfMutationVariables = Exact<{
  id: string | number
}>

export type DetachShearingCertificatePdfMutation = { detachShearingCertificatePdf: { id: string } | null }

export type ImportAdminOrderCsvMutationVariables = Exact<{
  tenantId: string
  csv: string
  fileName: string
  pdfs: Array<OrderPdfMappingInput> | OrderPdfMappingInput
}>

export type ImportAdminOrderCsvMutation = {
  importAdminOrderCsv: {
    id: string
    status: string
    summary: {
      totalRowCount: number
      createdOrderCount: number
      updatedOrderCount: number
      createdLineCount: number
      updatedLineCount: number
      deletedLineCount: number
      protectedLineCount: number
      registeredPdfCount: number
      errorRows: Array<{ rowNumber: number; message: string }>
    }
  }
}

export type ImportDeliveryTableCsvMutationVariables = Exact<{
  csv: string
  fileName: string
}>

export type ImportDeliveryTableCsvMutation = {
  importDeliveryTableCsv: {
    csvImport: {
      id: string
      tenantId: string
      kind: SteelOrderCsvImportKindEnum
      source: SteelOrderCsvImportSourceEnum
      fileName: string
      status: SteelOrderCsvImportStatusEnum
      createdByName: string | null
      createdAt: string
      summary: {
        totalRowCount: number
        emptyRowCount: number
        importedRowCount: number
        skippedContractNumbers: Array<string>
        errorRows: Array<{ rowNumber: number; message: string }>
        skippedSiteCodeRows: Array<{ siteCode: string; rowCount: number }>
      }
    } | null
    rowResults: Array<{
      index: number
      success: boolean
      isEmptyLine: boolean | null
      message: string | null
      resultTitle: string | null
      skippedByContractNumberError: boolean | null
      skippedByUnknownSiteCode: boolean | null
    }>
  }
}

export type ImportScrmCsvMutationVariables = Exact<{
  csv: string
  fileName: string
}>

export type ImportScrmCsvMutation = {
  importScrmCsv: {
    csvImport: {
      id: string
      tenantId: string
      kind: SteelOrderCsvImportKindEnum
      source: SteelOrderCsvImportSourceEnum
      fileName: string
      status: SteelOrderCsvImportStatusEnum
      createdByName: string | null
      createdAt: string
      summary: {
        totalRowCount: number
        emptyRowCount: number
        importedRowCount: number
        skippedContractNumbers: Array<string>
        errorRows: Array<{ rowNumber: number; message: string }>
        skippedSiteCodeRows: Array<{ siteCode: string; rowCount: number }>
      }
    } | null
    rowResults: Array<{
      index: number
      success: boolean
      isEmptyLine: boolean | null
      message: string | null
      resultTitle: string | null
      skippedByContractNumberError: boolean | null
      skippedByUnknownSiteCode: boolean | null
    }>
  }
}

export type PrepareAdminOrderPdfUploadMutationVariables = Exact<{
  tenantId: string
  fileName: string
  contentLength: number
}>

export type PrepareAdminOrderPdfUploadMutation = { prepareAdminOrderPdfUpload: { id: string; fileUrlForPut: string } }

export type PrepareOrderPdfUploadMutationVariables = Exact<{
  fileName: string
  contentLength: number
}>

export type PrepareOrderPdfUploadMutation = { prepareOrderPdfUpload: { id: string; fileUrlForPut: string } }

export type PreviewAdminOrderCsvMutationVariables = Exact<{
  tenantId: string
  csv: string
}>

export type PreviewAdminOrderCsvMutation = {
  previewAdminOrderCsv: Array<{
    projectName: string
    supplierName: string
    orderNumber: string
    sourceFileName: string
    itemCount: number
  }>
}

export type ReleaseOrderLineMatchingAllocationMutationVariables = Exact<{
  input: ReleaseOrderLineMatchingAllocationInput
}>

export type ReleaseOrderLineMatchingAllocationMutation = { releaseOrderLineMatchingAllocation: boolean }

export type RenameOrderProjectMutationVariables = Exact<{
  projectNameNormalized: string
  newProjectName: string
}>

export type RenameOrderProjectMutation = {
  renameOrderProject: { projectName: string; projectNameNormalized: string; updatedOrderCount: number; merged: boolean }
}

export type RestoreAdminIdentityMutationVariables = Exact<{
  input: RestoreAdminIdentityInput
}>

export type RestoreAdminIdentityMutation = {
  restoreAdminIdentity: {
    id: string
    email: string
    role: IdentityRoleEnum
    createdAt: string
    archivedAt: string | null
  }
}

export type RetryOrderOcrMutationVariables = Exact<{
  id: string
}>

export type RetryOrderOcrMutation = {
  retryOrderOcr: {
    id: string
    tenantId: string
    sourceFileName: string
    projectName: string
    projectNameFixed: boolean
    supplierName: string | null
    orderNumber: string | null
    orderedAt: string | null
    deliveryDueAt: string | null
    deliveryPlace: string | null
    remarks: string | null
    s3ObjectId: string
    status: OrderStatusEnum
    note: string | null
    uploadedAt: string
    registeredAt: string | null
    extractedAt: string | null
    extractionModel: string | null
    extractionError: string | null
    updatedAt: string
    ocrRetryAvailableAt: string | null
    confirmedAt: string | null
    confirmedByName: string | null
    itemCount: number
  }
}

export type UnconfirmMillsheetDuplicateGroupMutationVariables = Exact<{
  millsheetId: string | number
  itemIds: Array<string | number> | string | number
}>

export type UnconfirmMillsheetDuplicateGroupMutation = { unconfirmMillsheetDuplicateGroup: number }

export type UnlinkMillsheetS3ObjectsMutationVariables = Exact<{
  millsheetId: string | number
  s3ObjectIds: Array<string | number> | string | number
}>

export type UnlinkMillsheetS3ObjectsMutation = { unlinkMillsheetS3Objects: number }

export type UpdateAdminIdentityRoleMutationVariables = Exact<{
  input: UpdateAdminIdentityRoleInput
}>

export type UpdateAdminIdentityRoleMutation = {
  updateAdminIdentityRole: {
    id: string
    email: string
    role: IdentityRoleEnum
    createdAt: string
    archivedAt: string | null
  }
}

export type UpdateAdminPlanMutationVariables = Exact<{
  plan: PlanUpdateInput
}>

export type UpdateAdminPlanMutation = { updateAdminPlan: { id: string; name: string; serviceTerms: string } | null }

export type UpdateMillsheetMutationVariables = Exact<{
  input: MillsheetUpdateInput
}>

export type UpdateMillsheetMutation = {
  updateMillsheet: {
    id: string
    tenantId: string
    no: string
    manufacturerName: string
    issuedDate: string
    submissionDate: string | null
    tradingCompany: string
    customer: string
    constructionArea: string
    member: string
    buildingPart: string
    constructionName: string
    orderNumber: string
    innerNo: string
    steelOrderId: string | null
    millsheetItems: Array<{
      id: string
      heatNumber: string
      individualNumber: string
      pageNumber: number | null
      steelType: string
      sectionType: string
      constructionArea: string
      member: string
      buildingPart: string
      quantity: number
      size: string
      thicknessDiameter: string | null
      endorsementCompany: string | null
      endorsementDate: string | null
      endorsementCompany2: string | null
      endorsementDate2: string | null
      endorsementCompany3: string | null
      endorsementDate3: string | null
      endorsementCompany4: string | null
      endorsementDate4: string | null
      endorsementCompany5: string | null
      endorsementDate5: string | null
      endorsementCompany6: string | null
      endorsementDate6: string | null
      weightBeforeProcessing: number | null
      length: string
      rank: number
    }>
    s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum; thumbnailCount: number | null }>
    shearingCertificates: Array<{ id: string; certificateNo: string | null; s3Object: { id: string } | null }>
  } | null
}

export type UpdateMillsheetItemMutationVariables = Exact<{
  input: MillsheetItemUpdateInput
}>

export type UpdateMillsheetItemMutation = {
  updateMillsheetItem: {
    id: string
    heatNumber: string
    individualNumber: string
    pageNumber: number | null
    steelType: string
    sectionType: string
    constructionArea: string
    member: string
    buildingPart: string
    quantity: number
    size: string
    thicknessDiameter: string | null
    endorsementCompany: string | null
    endorsementDate: string | null
    endorsementCompany2: string | null
    endorsementDate2: string | null
    endorsementCompany3: string | null
    endorsementDate3: string | null
    endorsementCompany4: string | null
    endorsementDate4: string | null
    endorsementCompany5: string | null
    endorsementDate5: string | null
    endorsementCompany6: string | null
    endorsementDate6: string | null
    weightBeforeProcessing: number | null
    length: string
    rank: number
  } | null
}

export type UpdateMillsheetOrderNumberMutationVariables = Exact<{
  millsheetId: string | number
  orderNumber: string
}>

export type UpdateMillsheetOrderNumberMutation = { updateMillsheetOrderNumber: { steelOrderId: string | null } }

export type UpdateMillsheetsMutationVariables = Exact<{
  input: MillsheetsUpdateInput
}>

export type UpdateMillsheetsMutation = {
  updateMillsheets: Array<{
    id: string
    tenantId: string
    no: string
    manufacturerName: string
    issuedDate: string
    submissionDate: string | null
    tradingCompany: string
    customer: string
    constructionArea: string
    member: string
    buildingPart: string
    constructionName: string
    orderNumber: string
    innerNo: string
    steelOrderId: string | null
    millsheetItems: Array<{
      id: string
      heatNumber: string
      individualNumber: string
      pageNumber: number | null
      steelType: string
      sectionType: string
      constructionArea: string
      member: string
      buildingPart: string
      quantity: number
      size: string
      thicknessDiameter: string | null
      endorsementCompany: string | null
      endorsementDate: string | null
      endorsementCompany2: string | null
      endorsementDate2: string | null
      endorsementCompany3: string | null
      endorsementDate3: string | null
      endorsementCompany4: string | null
      endorsementDate4: string | null
      endorsementCompany5: string | null
      endorsementDate5: string | null
      endorsementCompany6: string | null
      endorsementDate6: string | null
      weightBeforeProcessing: number | null
      length: string
      rank: number
    }>
    s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum; thumbnailCount: number | null }>
    shearingCertificates: Array<{ id: string; certificateNo: string | null; s3Object: { id: string } | null }>
  }>
}

export type UpdateNestingDiagramMutationVariables = Exact<{
  id: string | number
  individualNumber: string
}>

export type UpdateNestingDiagramMutation = {
  updateNestingDiagram: {
    id: string
    individualNumber: string
    shearingCertificateItemId: string | null
    linkedCertificateNo: string | null
    createdAt: string
    s3Object: { id: string } | null
  } | null
}

export type UpdateOrderFromReviewMutationVariables = Exact<{
  id: string
  records: unknown
  expectedUpdatedAt: string
}>

export type UpdateOrderFromReviewMutation = {
  updateOrderFromReview: {
    id: string
    status: string
    summary: {
      totalRowCount: number
      createdOrderCount: number
      updatedOrderCount: number
      createdLineCount: number
      updatedLineCount: number
      deletedLineCount: number
      protectedLineCount: number
      registeredPdfCount: number
      errorRows: Array<{ rowNumber: number; message: string }>
    }
  }
}

export type UpdateOrderLineMatchingItemMutationVariables = Exact<{
  input: UpdateOrderLineMatchingItemInput
}>

export type UpdateOrderLineMatchingItemMutation = { updateOrderLineMatchingItem: boolean }

export type UpdateS3ObjectMutationVariables = Exact<{
  input: S3ObjectUpdateInput
}>

export type UpdateS3ObjectMutation = { updateS3Object: { id: string } | null }

export type UpdateSteelOrderItemQualityCheckStatusMutationVariables = Exact<{
  steelOrderItemId: string | number
  qualityCheckStatus: SteelOrderQualityCheckStatusEnum
}>

export type UpdateSteelOrderItemQualityCheckStatusMutation = { updateSteelOrderItemQualityCheckStatus: string }

export type UpdateSteelOrdersMutationVariables = Exact<{
  input: SteelOrdersUpdateInput
}>

export type UpdateSteelOrdersMutation = { updateSteelOrders: number }

export type UpdateTenantMutationVariables = Exact<{
  tenant: AdminTenantUpdateInput
}>

export type UpdateTenantMutation = {
  updateAdminTenant: {
    id: string
    name: string
    companyName: string
    prefecture: string
    memo: string
    status: TenantStatusEnum
    introductionDate: string | null
    lastConversationDate: string | null
    lastInterviewDate: string | null
    createdAt: string
    setting: {
      id: string
      orderLineMatchingEnabled: boolean
      orderMatchingEnabled: boolean
      scrmSiteCode: string | null
      millsheetSortConfig: {
        manufacturerName: Array<string> | null
        steelType: Array<string> | null
        sectionType: Array<string> | null
      } | null
    } | null
    users: Array<{
      id: string
      name: string
      email: string
      role: UserRoleEnum
      smsMfaRequired: boolean
      createdAt: string
      signedUpAt: string | null
    }>
    tenantPlan: {
      id: string
      planId: string
      quoteNumber: string
      plan: { id: string; name: string; serviceTerms: string }
    } | null
  } | null
}

export type UpdateTenantSettingMutationVariables = Exact<{
  input: TenantSettingUpdateInput
}>

export type UpdateTenantSettingMutation = {
  updateTenantSetting: {
    id: string
    orderLineMatchingEnabled: boolean
    orderMatchingEnabled: boolean
    scrmSiteCode: string | null
    millsheetSortConfig: {
      manufacturerName: Array<string> | null
      steelType: Array<string> | null
      sectionType: Array<string> | null
    } | null
  } | null
}

export type UpdateUserMutationVariables = Exact<{
  input: UserInput
}>

export type UpdateUserMutation = {
  updateUser: {
    id: string
    name: string
    email: string
    role: UserRoleEnum
    smsMfaRequired: boolean
    createdAt: string
    signedUpAt: string | null
  } | null
}

export type UpsertAdminShapeNameConversionMutationVariables = Exact<{
  input: AdminShapeNameConversionUpsertInput
}>

export type UpsertAdminShapeNameConversionMutation = {
  upsertAdminShapeNameConversion: {
    id: string
    tenantId: string
    field: ShapeNameConversionFieldEnum
    sourceValue: string
    normalizedValue: string
    createdAt: string
    updatedAt: string
  }
}

export type UpsertAdminTenantSettingMutationVariables = Exact<{
  tenantSetting: AdminTenantSettingUpdateInput
}>

export type UpsertAdminTenantSettingMutation = {
  upsertAdminTenantSetting: {
    id: string
    orderLineMatchingEnabled: boolean
    orderMatchingEnabled: boolean
    scrmSiteCode: string | null
    millsheetSortConfig: {
      manufacturerName: Array<string> | null
      steelType: Array<string> | null
      sectionType: Array<string> | null
    } | null
  }
}

export type UpsertOrderLineMatchingAllocationMutationVariables = Exact<{
  input: UpsertOrderLineMatchingAllocationInput
}>

export type UpsertOrderLineMatchingAllocationMutation = { upsertOrderLineMatchingAllocation: boolean }

export type FetchAdminS3ObjectQueryVariables = Exact<{
  id: string | number
}>

export type FetchAdminS3ObjectQuery = {
  adminS3Object: {
    id: string
    originalFileName: string
    resourceType: S3ObjectResourceTypeEnum
    extension: string
    contentType: string
    contentLength: number
    thumbnailCount: number | null
    imageWidth: number | null
    imageHeight: number | null
    fileUrl: string
    createdAt: string
    thumbnailFileUrls: Array<string>
  }
}

export type FetchAdminShapeNameConversionsQueryVariables = Exact<{
  tenantId: string
}>

export type FetchAdminShapeNameConversionsQuery = {
  adminShapeNameConversions: Array<{
    id: string
    tenantId: string
    field: ShapeNameConversionFieldEnum
    sourceValue: string
    normalizedValue: string
    createdAt: string
    updatedAt: string
  }>
}

export type FetchAdminTenantsQueryVariables = Exact<{ [key: string]: never }>

export type FetchAdminTenantsQuery = {
  adminTenants: {
    total: number
    tenants: Array<{
      id: string
      name: string
      companyName: string
      prefecture: string
      memo: string
      status: TenantStatusEnum
      introductionDate: string | null
      lastConversationDate: string | null
      lastInterviewDate: string | null
      createdAt: string
      setting: {
        id: string
        orderLineMatchingEnabled: boolean
        orderMatchingEnabled: boolean
        scrmSiteCode: string | null
        millsheetSortConfig: {
          manufacturerName: Array<string> | null
          steelType: Array<string> | null
          sectionType: Array<string> | null
        } | null
      } | null
      users: Array<{
        id: string
        name: string
        email: string
        role: UserRoleEnum
        smsMfaRequired: boolean
        createdAt: string
        signedUpAt: string | null
      }>
      tenantPlan: {
        id: string
        planId: string
        quoteNumber: string
        plan: { id: string; name: string; serviceTerms: string }
      } | null
    }>
  }
}

export type FetchDashboardQueryVariables = Exact<{ [key: string]: never }>

export type FetchDashboardQuery = {
  dashboard: {
    millsheetsCount: number
    tenantPlan: { id: string; quoteNumber: string; plan: { id: string; name: string; serviceTerms: string } } | null
  }
}

export type FetchMeQueryVariables = Exact<{ [key: string]: never }>

export type FetchMeQuery = {
  me: {
    id: string
    name: string
    email: string
    imageUrl: string | null
    role: UserRoleEnum
    tenant: {
      id: string
      name: string
      companyName: string
      prefecture: string
      memo: string
      status: TenantStatusEnum
      createdAt: string
      setting: {
        id: string
        orderLineMatchingEnabled: boolean
        orderMatchingEnabled: boolean
        scrmSiteCode: string | null
        millsheetSortConfig: {
          manufacturerName: Array<string> | null
          steelType: Array<string> | null
          sectionType: Array<string> | null
        } | null
      } | null
    }
  }
}

export type FetchMillsheetDuplicateGroupCountQueryVariables = Exact<{
  unconfirmedOnly: boolean
}>

export type FetchMillsheetDuplicateGroupCountQuery = { millsheetDuplicateGroupCount: number }

export type FetchMillsheetDuplicateGroupsQueryVariables = Exact<{
  input: MillsheetDuplicateGroupsInput
  unconfirmedOnly: boolean
}>

export type FetchMillsheetDuplicateGroupsQuery = {
  millsheetDuplicateGroupCount: number
  millsheetDuplicateGroups: Array<{
    key: string
    confirmed: boolean
    millsheet: {
      id: string
      no: string
      constructionName: string
      orderNumber: string
      s3Objects: Array<{ id: string; originalFileName: string }>
    }
    items: Array<{
      id: string
      heatNumber: string
      individualNumber: string
      pageNumber: number | null
      steelType: string
      sectionType: string
      constructionArea: string
      member: string
      buildingPart: string
      quantity: number
      size: string
      thicknessDiameter: string | null
      endorsementCompany: string | null
      endorsementDate: string | null
      endorsementCompany2: string | null
      endorsementDate2: string | null
      endorsementCompany3: string | null
      endorsementDate3: string | null
      endorsementCompany4: string | null
      endorsementDate4: string | null
      endorsementCompany5: string | null
      endorsementDate5: string | null
      endorsementCompany6: string | null
      endorsementDate6: string | null
      weightBeforeProcessing: number | null
      length: string
      rank: number
    }>
  }>
}

export type FetchMillsheetItemsQueryVariables = Exact<{
  heatNumber: string
}>

export type FetchMillsheetItemsQuery = {
  millsheetItems: Array<{
    millsheetNo: string | null
    id: string
    heatNumber: string
    individualNumber: string
    pageNumber: number | null
    steelType: string
    sectionType: string
    constructionArea: string
    member: string
    buildingPart: string
    quantity: number
    size: string
    thicknessDiameter: string | null
    endorsementCompany: string | null
    endorsementDate: string | null
    endorsementCompany2: string | null
    endorsementDate2: string | null
    endorsementCompany3: string | null
    endorsementDate3: string | null
    endorsementCompany4: string | null
    endorsementDate4: string | null
    endorsementCompany5: string | null
    endorsementDate5: string | null
    endorsementCompany6: string | null
    endorsementDate6: string | null
    weightBeforeProcessing: number | null
    length: string
    rank: number
  }>
}

export type FetchMillsheetUniqueValuesQueryVariables = Exact<{ [key: string]: never }>

export type FetchMillsheetUniqueValuesQuery = {
  millsheetUniqueValues: { manufacturerNames: Array<string>; steelTypes: Array<string>; sectionTypes: Array<string> }
}

export type FetchMillsheetsQueryVariables = Exact<{ [key: string]: never }>

export type FetchMillsheetsQuery = {
  millsheets: Array<{
    steelOrderReferenceNumber: string | null
    id: string
    tenantId: string
    no: string
    manufacturerName: string
    issuedDate: string
    submissionDate: string | null
    tradingCompany: string
    customer: string
    constructionArea: string
    member: string
    buildingPart: string
    constructionName: string
    orderNumber: string
    innerNo: string
    steelOrderId: string | null
    millsheetItems: Array<{
      id: string
      heatNumber: string
      individualNumber: string
      pageNumber: number | null
      steelType: string
      sectionType: string
      constructionArea: string
      member: string
      buildingPart: string
      quantity: number
      size: string
      thicknessDiameter: string | null
      endorsementCompany: string | null
      endorsementDate: string | null
      endorsementCompany2: string | null
      endorsementDate2: string | null
      endorsementCompany3: string | null
      endorsementDate3: string | null
      endorsementCompany4: string | null
      endorsementDate4: string | null
      endorsementCompany5: string | null
      endorsementDate5: string | null
      endorsementCompany6: string | null
      endorsementDate6: string | null
      weightBeforeProcessing: number | null
      length: string
      rank: number
    }>
    s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum; thumbnailCount: number | null }>
    shearingCertificates: Array<{ id: string; certificateNo: string | null; s3Object: { id: string } | null }>
  }>
}

export type FetchMillsheetsFilterOptionsQueryVariables = Exact<{ [key: string]: never }>

export type FetchMillsheetsFilterOptionsQuery = {
  millsheetsFilterOptions: {
    manufacturers: Array<string>
    tradingCompanies: Array<string>
    customers: Array<string>
    constructionNames: Array<string>
    constructionAreas: Array<string>
    members: Array<string>
    buildingParts: Array<string>
    steelTypes: Array<string>
    steelSectionTypes: Array<string>
    submissionDates: Array<string>
    endorsementCompanies: Array<string>
  }
}

export type FetchMyIdentityQueryVariables = Exact<{ [key: string]: never }>

export type FetchMyIdentityQuery = {
  myIdentity: {
    id: string
    email: string
    role: IdentityRoleEnum
    users: Array<{
      id: string
      name: string
      email: string
      imageUrl: string | null
      role: UserRoleEnum
      tenant: {
        id: string
        name: string
        companyName: string
        prefecture: string
        memo: string
        status: TenantStatusEnum
        createdAt: string
        setting: {
          id: string
          orderLineMatchingEnabled: boolean
          orderMatchingEnabled: boolean
          scrmSiteCode: string | null
          millsheetSortConfig: {
            manufacturerName: Array<string> | null
            steelType: Array<string> | null
            sectionType: Array<string> | null
          } | null
        } | null
      }
    }>
  }
}

export type FetchMyTenantDetailQueryVariables = Exact<{ [key: string]: never }>

export type FetchMyTenantDetailQuery = {
  me: {
    id: string
    name: string
    tenant: {
      id: string
      name: string
      tenantPlan: { id: string; quoteNumber: string; plan: { id: string; name: string; serviceTerms: string } } | null
    }
  }
}

export type FetchNestingDiagramsQueryVariables = Exact<{ [key: string]: never }>

export type FetchNestingDiagramsQuery = {
  nestingDiagrams: Array<{
    id: string
    individualNumber: string
    shearingCertificateItemId: string | null
    linkedCertificateNo: string | null
    createdAt: string
    s3Object: { id: string } | null
  }>
}

export type FetchOrderQueryVariables = Exact<{
  id: string
}>

export type FetchOrderQuery = {
  orderOcrEnabled: boolean
  order: {
    extraction: unknown
    id: string
    tenantId: string
    sourceFileName: string
    projectName: string
    projectNameFixed: boolean
    supplierName: string | null
    orderNumber: string | null
    orderedAt: string | null
    deliveryDueAt: string | null
    deliveryPlace: string | null
    remarks: string | null
    s3ObjectId: string
    status: OrderStatusEnum
    note: string | null
    uploadedAt: string
    registeredAt: string | null
    extractedAt: string | null
    extractionModel: string | null
    extractionError: string | null
    updatedAt: string
    ocrRetryAvailableAt: string | null
    confirmedAt: string | null
    confirmedByName: string | null
    itemCount: number
    items: Array<{
      id: string
      lineNo: number
      steelType: string
      sectionType: string
      size: string
      length: number | null
      quantity: number | null
      weight: number | null
      plateNumber: string | null
      buildingPart: string | null
      constructionArea: string | null
      editedAt: string | null
    }>
  }
}

export type FetchOrderLineMatchingPdfUrlQueryVariables = Exact<{
  tenantId?: string | number | null | undefined
  s3ObjectId: string | number
}>

export type FetchOrderLineMatchingPdfUrlQuery = { orderLineMatchingPdfUrl: string }

export type FetchOrderLineMatchingProjectQueryVariables = Exact<{
  input: OrderLineMatchingProjectInput
}>

export type FetchOrderLineMatchingProjectQuery = {
  orderLineMatchingProject: {
    projectName: string
    totalCount: number
    orderCount: number
    matrix: Array<{ supplierName: string; sectionType: string; weight: number; allocatedWeight: number }>
    orders: Array<{
      id: string
      orderNumber: string
      supplierName: string
      orderedAt: string | null
      deliveryDueAt: string | null
      s3ObjectId: string
      sourceFileName: string
      items: Array<{
        id: string
        lineNo: number
        steelType: string
        sectionType: string
        size: string
        length: number | null
        quantity: number | null
        weight: number | null
        plateNumber: string | null
        arrivalStatus: OrderLineMatchingArrivalStatus
        allocatedQuantity: number
        allocatedWeight: number
        weightDiff: number | null
        weightDiffRate: number | null
        needsReview: boolean
        editedAt: string | null
        reviewReasons: Array<OrderLineMatchingReviewReason>
        allocations: Array<{
          id: string
          millsheetId: string
          millsheetItemId: string
          certificateNumber: string
          itemNo: number
          quantity: number | null
          originalQuantity: number | null
          weight: number
          method: OrderLineMatchingMethod
          confidence: OrderLineMatchingConfidence
          pdfs: Array<{ s3ObjectId: string; page: number | null }>
        }>
      }>
    }>
  }
}

export type FetchOrderLineMatchingProjectsQueryVariables = Exact<{
  input: OrderLineMatchingProjectsInput
}>

export type FetchOrderLineMatchingProjectsQuery = {
  orderLineMatchingProjects: {
    totalCount: number
    projects: Array<{
      projectKey: string
      projectName: string
      orderCount: number
      itemCount: number
      arrivedItemCount: number
      reviewItemCount: number
      notArrivedItemCount: number
      totalWeight: number
      allocatedWeight: number
      updatedAt: string
    }>
  }
}

export type FetchOrderLineMatchingUnlinkedFilterOptionsQueryVariables = Exact<{ [key: string]: never }>

export type FetchOrderLineMatchingUnlinkedFilterOptionsQuery = {
  orderLineMatchingUnlinkedFilterOptions: {
    manufacturers: Array<string>
    tradingCompanies: Array<string>
    customers: Array<string>
    constructionNames: Array<string>
    constructionAreas: Array<string>
    members: Array<string>
    buildingParts: Array<string>
    steelTypes: Array<string>
    steelSectionTypes: Array<string>
    submissionDates: Array<string>
    endorsementCompanies: Array<string>
  }
}

export type FetchOrderLineMatchingUnlinkedItemsQueryVariables = Exact<{
  input: OrderLineMatchingUnlinkedInput
}>

export type FetchOrderLineMatchingUnlinkedItemsQuery = {
  orderLineMatchingUnlinkedItems: {
    totalCount: number
    items: Array<{
      id: string
      millsheetId: string
      itemNo: number
      certificateNumber: string
      individualNumber: string
      steelType: string
      sectionType: string
      size: string
      length: string
      issuedDate: string
      constructionName: string
      manufacturerName: string
      tradingCompany: string
      endorsementCompany: string | null
      totalOriginalQuantity: number
      usedOriginalQuantity: number
      remainingOriginalQuantity: number
      allocatableOriginalQuantity: number
      totalWeight: number | null
      usedWeight: number
      remainingWeight: number | null
      allocationCount: number
    }>
  }
}

export type FetchOrderOcrEnabledQueryVariables = Exact<{ [key: string]: never }>

export type FetchOrderOcrEnabledQuery = { orderOcrEnabled: boolean }

export type FetchOrderPdfUrlQueryVariables = Exact<{
  id: string
}>

export type FetchOrderPdfUrlQuery = { orderPdfUrl: string }

export type FetchOrderProjectNamesQueryVariables = Exact<{
  includeUnregistered?: boolean | null | undefined
}>

export type FetchOrderProjectNamesQuery = { orderProjectNames: Array<string> }

export type FetchOrdersQueryVariables = Exact<{
  ids?: Array<string> | string | null | undefined
  page?: number | null | undefined
  pageSize?: number | null | undefined
}>

export type FetchOrdersQuery = {
  orders: {
    totalCount: number
    orders: Array<{
      id: string
      tenantId: string
      sourceFileName: string
      projectName: string
      projectNameFixed: boolean
      supplierName: string | null
      orderNumber: string | null
      orderedAt: string | null
      deliveryDueAt: string | null
      deliveryPlace: string | null
      remarks: string | null
      s3ObjectId: string
      status: OrderStatusEnum
      note: string | null
      uploadedAt: string
      registeredAt: string | null
      extractedAt: string | null
      extractionModel: string | null
      extractionError: string | null
      updatedAt: string
      ocrRetryAvailableAt: string | null
      confirmedAt: string | null
      confirmedByName: string | null
      itemCount: number
    }>
  }
}

export type FetchPagedAdminIdentitiesQueryVariables = Exact<{
  input?: PagedAdminIdentitiesInput | null | undefined
}>

export type FetchPagedAdminIdentitiesQuery = {
  pagedAdminIdentities: {
    totalCount: number
    items: Array<{ id: string; email: string; role: IdentityRoleEnum; createdAt: string; archivedAt: string | null }>
  }
}

export type FetchPagedMillsheetItemsQueryVariables = Exact<{
  heatNumber: string
  page?: number | null | undefined
  pageSize?: number | null | undefined
}>

export type FetchPagedMillsheetItemsQuery = {
  pagedMillsheetItems: {
    totalCount: number
    millsheetItems: Array<{
      millsheetNo: string | null
      id: string
      heatNumber: string
      individualNumber: string
      pageNumber: number | null
      steelType: string
      sectionType: string
      constructionArea: string
      member: string
      buildingPart: string
      quantity: number
      size: string
      thicknessDiameter: string | null
      endorsementCompany: string | null
      endorsementDate: string | null
      endorsementCompany2: string | null
      endorsementDate2: string | null
      endorsementCompany3: string | null
      endorsementDate3: string | null
      endorsementCompany4: string | null
      endorsementDate4: string | null
      endorsementCompany5: string | null
      endorsementDate5: string | null
      endorsementCompany6: string | null
      endorsementDate6: string | null
      weightBeforeProcessing: number | null
      length: string
      rank: number
    }>
  }
}

export type FetchPagedMillsheetsQueryVariables = Exact<{
  input: MillsheetsSearchInput
}>

export type FetchPagedMillsheetsQuery = {
  pagedMillsheets: {
    totalCount: number
    millsheets: Array<{
      steelOrderReferenceNumber: string | null
      id: string
      tenantId: string
      no: string
      manufacturerName: string
      issuedDate: string
      submissionDate: string | null
      tradingCompany: string
      customer: string
      constructionArea: string
      member: string
      buildingPart: string
      constructionName: string
      orderNumber: string
      innerNo: string
      steelOrderId: string | null
      millsheetItems: Array<{
        id: string
        heatNumber: string
        individualNumber: string
        pageNumber: number | null
        steelType: string
        sectionType: string
        constructionArea: string
        member: string
        buildingPart: string
        quantity: number
        size: string
        thicknessDiameter: string | null
        endorsementCompany: string | null
        endorsementDate: string | null
        endorsementCompany2: string | null
        endorsementDate2: string | null
        endorsementCompany3: string | null
        endorsementDate3: string | null
        endorsementCompany4: string | null
        endorsementDate4: string | null
        endorsementCompany5: string | null
        endorsementDate5: string | null
        endorsementCompany6: string | null
        endorsementDate6: string | null
        weightBeforeProcessing: number | null
        length: string
        rank: number
      }>
      s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum; thumbnailCount: number | null }>
      shearingCertificates: Array<{ id: string; certificateNo: string | null; s3Object: { id: string } | null }>
    }>
  }
}

export type FetchPagedNestingDiagramsQueryVariables = Exact<{
  s3ObjectIds?: Array<string> | string | null | undefined
  page?: number | null | undefined
  pageSize?: number | null | undefined
}>

export type FetchPagedNestingDiagramsQuery = {
  pagedNestingDiagrams: {
    totalCount: number
    nestingDiagrams: Array<{
      id: string
      individualNumber: string
      shearingCertificateItemId: string | null
      linkedCertificateNo: string | null
      createdAt: string
      s3Object: { id: string } | null
    }>
  }
}

export type FetchPagedS3ObjectsQueryVariables = Exact<{
  ids?: Array<string> | string | null | undefined
  notConnectedOnly?: boolean | null | undefined
  resourceType?: S3ObjectResourceTypeEnum | null | undefined
  page?: number | null | undefined
  pageSize?: number | null | undefined
}>

export type FetchPagedS3ObjectsQuery = {
  pagedS3Objects: {
    totalCount: number
    s3Objects: Array<{
      id: string
      originalFileName: string
      resourceType: S3ObjectResourceTypeEnum
      extension: string
      contentType: string
      contentLength: number
      thumbnailCount: number | null
      imageWidth: number | null
      imageHeight: number | null
      fileUrl: string
      createdAt: string
      thumbnailFileUrls: Array<string>
      linkedMillsheets: Array<{ id: string; no: string; constructionName: string; orderNumber: string }>
    }>
  }
}

export type FetchPagedShearingCertificatePdfsQueryVariables = Exact<{
  ids?: Array<string> | string | null | undefined
  resourceType?: S3ObjectResourceTypeEnum | null | undefined
  page?: number | null | undefined
  pageSize?: number | null | undefined
}>

export type FetchPagedShearingCertificatePdfsQuery = {
  pagedS3Objects: {
    totalCount: number
    s3Objects: Array<{
      id: string
      originalFileName: string
      createdAt: string
      linkedShearingCertificateId: string | null
      linkedShearingCertificateNo: string | null
    }>
  }
}

export type FetchPagedShearingCertificatesQueryVariables = Exact<{
  input: ShearingCertificatesSearchInput
}>

export type FetchPagedShearingCertificatesQuery = {
  pagedShearingCertificates: {
    totalCount: number
    shearingCertificates: Array<{
      id: string
      tenantId: string
      issuingCompanyName: string
      issuedDate: string
      certificateNo: string | null
      constructionName: string
      items: Array<{
        id: string
        steelType: string
        thickness: string
        weight: number | null
        no: string
        individualNumber: string
        manufacturerName: string
        rank: number
        millsheets: Array<{ id: string; s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum }> }>
        nestingDiagrams: Array<{
          id: string
          individualNumber: string
          shearingCertificateItemId: string | null
          s3Object: { id: string } | null
        }>
      }>
      s3Object: { id: string } | null
    }>
  }
}

export type FetchPagedSteelOrderCsvImportsQueryVariables = Exact<{
  page?: number | null | undefined
  pageSize?: number | null | undefined
}>

export type FetchPagedSteelOrderCsvImportsQuery = {
  pagedSteelOrderCsvImports: {
    totalCount: number
    steelOrderCsvImports: Array<{
      id: string
      tenantId: string
      kind: SteelOrderCsvImportKindEnum
      source: SteelOrderCsvImportSourceEnum
      fileName: string
      status: SteelOrderCsvImportStatusEnum
      createdByName: string | null
      createdAt: string
      summary: {
        totalRowCount: number
        emptyRowCount: number
        importedRowCount: number
        skippedContractNumbers: Array<string>
        errorRows: Array<{ rowNumber: number; message: string }>
        skippedSiteCodeRows: Array<{ siteCode: string; rowCount: number }>
      }
    }>
  }
}

export type FetchPagedUnlinkedMillsheetsQueryVariables = Exact<{
  input: MillsheetsSearchInput
}>

export type FetchPagedUnlinkedMillsheetsQuery = {
  pagedUnlinkedMillsheets: {
    totalCount: number
    millsheets: Array<{
      id: string
      tenantId: string
      no: string
      manufacturerName: string
      issuedDate: string
      submissionDate: string | null
      tradingCompany: string
      customer: string
      constructionArea: string
      member: string
      buildingPart: string
      constructionName: string
      orderNumber: string
      innerNo: string
      steelOrderId: string | null
      millsheetItems: Array<{
        id: string
        heatNumber: string
        individualNumber: string
        pageNumber: number | null
        steelType: string
        sectionType: string
        constructionArea: string
        member: string
        buildingPart: string
        quantity: number
        size: string
        thicknessDiameter: string | null
        endorsementCompany: string | null
        endorsementDate: string | null
        endorsementCompany2: string | null
        endorsementDate2: string | null
        endorsementCompany3: string | null
        endorsementDate3: string | null
        endorsementCompany4: string | null
        endorsementDate4: string | null
        endorsementCompany5: string | null
        endorsementDate5: string | null
        endorsementCompany6: string | null
        endorsementDate6: string | null
        weightBeforeProcessing: number | null
        length: string
        rank: number
      }>
      s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum; thumbnailCount: number | null }>
      shearingCertificates: Array<{ id: string; certificateNo: string | null; s3Object: { id: string } | null }>
    }>
  }
}

export type FetchAdminPlansQueryVariables = Exact<{ [key: string]: never }>

export type FetchAdminPlansQuery = { adminPlans: Array<{ id: string; name: string; serviceTerms: string }> }

export type FetchS3ObjectsQueryVariables = Exact<{
  ids?: Array<string> | string | null | undefined
  notConnectedOnly?: boolean | null | undefined
  resourceType?: S3ObjectResourceTypeEnum | null | undefined
}>

export type FetchS3ObjectsQuery = {
  s3Objects: Array<{
    id: string
    originalFileName: string
    resourceType: S3ObjectResourceTypeEnum
    extension: string
    contentType: string
    contentLength: number
    thumbnailCount: number | null
    imageWidth: number | null
    imageHeight: number | null
    fileUrl: string
    createdAt: string
    thumbnailFileUrls: Array<string>
    linkedMillsheets: Array<{ id: string; no: string; constructionName: string; orderNumber: string }>
  }>
}

export type FetchShapeNameConversionsQueryVariables = Exact<{ [key: string]: never }>

export type FetchShapeNameConversionsQuery = {
  shapeNameConversions: Array<{
    id: string
    tenantId: string
    field: ShapeNameConversionFieldEnum
    sourceValue: string
    normalizedValue: string
    createdAt: string
    updatedAt: string
  }>
}

export type FetchShearingCertificatePdfsQueryVariables = Exact<{
  resourceType?: S3ObjectResourceTypeEnum | null | undefined
}>

export type FetchShearingCertificatePdfsQuery = {
  s3Objects: Array<{
    id: string
    originalFileName: string
    createdAt: string
    linkedShearingCertificateId: string | null
    linkedShearingCertificateNo: string | null
  }>
}

export type FetchShearingCertificateUniqueValuesQueryVariables = Exact<{ [key: string]: never }>

export type FetchShearingCertificateUniqueValuesQuery = {
  shearingCertificateUniqueValues: {
    issuingCompanyNames: Array<string>
    constructionNames: Array<string>
    steelTypes: Array<string>
    manufacturerNames: Array<string>
  }
}

export type FetchShearingCertificatesQueryVariables = Exact<{ [key: string]: never }>

export type FetchShearingCertificatesQuery = {
  shearingCertificates: Array<{
    id: string
    tenantId: string
    issuingCompanyName: string
    issuedDate: string
    certificateNo: string | null
    constructionName: string
    items: Array<{
      id: string
      steelType: string
      thickness: string
      weight: number | null
      no: string
      individualNumber: string
      manufacturerName: string
      rank: number
      millsheets: Array<{ id: string; s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum }> }>
      nestingDiagrams: Array<{
        id: string
        individualNumber: string
        shearingCertificateItemId: string | null
        s3Object: { id: string } | null
      }>
    }>
    s3Object: { id: string } | null
  }>
}

export type FetchShearingCertificatesFilterOptionsQueryVariables = Exact<{ [key: string]: never }>

export type FetchShearingCertificatesFilterOptionsQuery = {
  shearingCertificatesFilterOptions: {
    issuingCompanies: Array<string>
    constructionNames: Array<string>
    steelTypes: Array<string>
    manufacturerNames: Array<string>
  }
}

export type FetchSteelOrderCsvImportsQueryVariables = Exact<{ [key: string]: never }>

export type FetchSteelOrderCsvImportsQuery = {
  steelOrderCsvImports: Array<{
    id: string
    tenantId: string
    kind: SteelOrderCsvImportKindEnum
    source: SteelOrderCsvImportSourceEnum
    fileName: string
    status: SteelOrderCsvImportStatusEnum
    createdByName: string | null
    createdAt: string
    summary: {
      totalRowCount: number
      emptyRowCount: number
      importedRowCount: number
      skippedContractNumbers: Array<string>
      errorRows: Array<{ rowNumber: number; message: string }>
      skippedSiteCodeRows: Array<{ siteCode: string; rowCount: number }>
    }
  }>
}

export type FetchSteelOrderDetailQueryVariables = Exact<{
  input: SteelOrdersSearchInput
}>

export type FetchSteelOrderDetailQuery = {
  steelOrders: {
    totalCount: number
    steelOrders: Array<{
      id: string
      tenantId: string
      referenceNumber: string
      category: SteelOrderCategoryEnum
      millsheetStatus: SteelOrderMatchingStatusEnum
      isMillsheetExcluded: boolean
      orderStatus: SteelOrderMatchingStatusEnum
      qualityCheckStatus: SteelOrderQualityCheckStatusEnum
      outputDate: string | null
      supplierName: string | null
      importedAt: string
      totalWeight: number
      millsheetWeight: number
      orderedWeight: number
      millsheets: Array<{
        id: string
        no: string
        orderNumber: string
        constructionName: string
        tenantId: string
        manufacturerName: string
        issuedDate: string
        submissionDate: string | null
        tradingCompany: string
        customer: string
        constructionArea: string
        member: string
        buildingPart: string
        innerNo: string
        steelOrderId: string | null
        s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum; thumbnailCount: number | null }>
        millsheetItems: Array<{
          id: string
          heatNumber: string
          individualNumber: string
          pageNumber: number | null
          steelType: string
          sectionType: string
          constructionArea: string
          member: string
          buildingPart: string
          quantity: number
          size: string
          thicknessDiameter: string | null
          endorsementCompany: string | null
          endorsementDate: string | null
          endorsementCompany2: string | null
          endorsementDate2: string | null
          endorsementCompany3: string | null
          endorsementDate3: string | null
          endorsementCompany4: string | null
          endorsementDate4: string | null
          endorsementCompany5: string | null
          endorsementDate5: string | null
          endorsementCompany6: string | null
          endorsementDate6: string | null
          weightBeforeProcessing: number | null
          length: string
          rank: number
        }>
        shearingCertificates: Array<{ id: string; certificateNo: string | null; s3Object: { id: string } | null }>
      }>
      items: Array<{
        id: string
        tenantId: string
        steelOrderId: string
        managementNumber: string | null
        constructionNumber: string | null
        constructionName: string | null
        productName: string | null
        standard: string | null
        size: string | null
        length: string | null
        quantity: number | null
        weight: number | null
        manufacturerName: string | null
        itemNumber: string | null
        answeredDeliveryDate: string | null
        constructionNote: string | null
        qualityCheckStatus: SteelOrderQualityCheckStatusEnum
        qualityCheckUpdatedAt: string | null
        rank: number
        createdAt: string
      }>
    }>
  }
}

export type FetchSteelOrdersQueryVariables = Exact<{
  input: SteelOrdersSearchInput
}>

export type FetchSteelOrdersQuery = {
  steelOrders: {
    totalCount: number
    steelOrders: Array<{
      id: string
      tenantId: string
      referenceNumber: string
      category: SteelOrderCategoryEnum
      millsheetStatus: SteelOrderMatchingStatusEnum
      isMillsheetExcluded: boolean
      orderStatus: SteelOrderMatchingStatusEnum
      qualityCheckStatus: SteelOrderQualityCheckStatusEnum
      outputDate: string | null
      supplierName: string | null
      importedAt: string
      totalWeight: number
      millsheetWeight: number
      orderedWeight: number
      items: Array<{
        id: string
        tenantId: string
        steelOrderId: string
        managementNumber: string | null
        constructionNumber: string | null
        constructionName: string | null
        productName: string | null
        standard: string | null
        size: string | null
        length: string | null
        quantity: number | null
        weight: number | null
        manufacturerName: string | null
        itemNumber: string | null
        answeredDeliveryDate: string | null
        constructionNote: string | null
        qualityCheckStatus: SteelOrderQualityCheckStatusEnum
        qualityCheckUpdatedAt: string | null
        rank: number
        createdAt: string
      }>
      millsheets: Array<{
        id: string
        no: string
        orderNumber: string
        constructionName: string
        s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum }>
      }>
    }>
  }
}

export type FetchSteelOrdersForPrintQueryVariables = Exact<{
  input: SteelOrdersSearchInput
}>

export type FetchSteelOrdersForPrintQuery = {
  steelOrders: {
    totalCount: number
    steelOrders: Array<{
      id: string
      referenceNumber: string
      supplierName: string | null
      category: SteelOrderCategoryEnum
      millsheets: Array<{
        id: string
        tenantId: string
        no: string
        manufacturerName: string
        issuedDate: string
        submissionDate: string | null
        tradingCompany: string
        customer: string
        constructionArea: string
        member: string
        buildingPart: string
        constructionName: string
        orderNumber: string
        innerNo: string
        steelOrderId: string | null
        millsheetItems: Array<{
          id: string
          heatNumber: string
          individualNumber: string
          pageNumber: number | null
          steelType: string
          sectionType: string
          constructionArea: string
          member: string
          buildingPart: string
          quantity: number
          size: string
          thicknessDiameter: string | null
          endorsementCompany: string | null
          endorsementDate: string | null
          endorsementCompany2: string | null
          endorsementDate2: string | null
          endorsementCompany3: string | null
          endorsementDate3: string | null
          endorsementCompany4: string | null
          endorsementDate4: string | null
          endorsementCompany5: string | null
          endorsementDate5: string | null
          endorsementCompany6: string | null
          endorsementDate6: string | null
          weightBeforeProcessing: number | null
          length: string
          rank: number
        }>
        s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum; thumbnailCount: number | null }>
        shearingCertificates: Array<{ id: string; certificateNo: string | null; s3Object: { id: string } | null }>
      }>
    }>
  }
}

export type FetchTenantQueryVariables = Exact<{
  id: string | number
}>

export type FetchTenantQuery = {
  adminTenant: {
    id: string
    name: string
    companyName: string
    prefecture: string
    memo: string
    status: TenantStatusEnum
    introductionDate: string | null
    lastConversationDate: string | null
    lastInterviewDate: string | null
    createdAt: string
    setting: {
      id: string
      orderLineMatchingEnabled: boolean
      orderMatchingEnabled: boolean
      scrmSiteCode: string | null
      millsheetSortConfig: {
        manufacturerName: Array<string> | null
        steelType: Array<string> | null
        sectionType: Array<string> | null
      } | null
    } | null
    users: Array<{
      id: string
      name: string
      email: string
      role: UserRoleEnum
      smsMfaRequired: boolean
      createdAt: string
      signedUpAt: string | null
    }>
    tenantPlan: {
      id: string
      planId: string
      quoteNumber: string
      plan: { id: string; name: string; serviceTerms: string }
    } | null
  }
}

export type FetchTenantsQueryVariables = Exact<{ [key: string]: never }>

export type FetchTenantsQuery = {
  adminTenants: {
    total: number
    tenants: Array<{
      id: string
      name: string
      companyName: string
      prefecture: string
      status: TenantStatusEnum
      memo: string
      introductionDate: string | null
      lastConversationDate: string | null
      lastInterviewDate: string | null
      createdAt: string
      tenantPlan: {
        id: string
        planId: string
        quoteNumber: string
        plan: { id: string; name: string; serviceTerms: string }
      } | null
    }>
  }
}

export type FetchUnlinkedMillsheetsQueryVariables = Exact<{ [key: string]: never }>

export type FetchUnlinkedMillsheetsQuery = {
  unlinkedMillsheets: Array<{
    id: string
    tenantId: string
    no: string
    manufacturerName: string
    issuedDate: string
    submissionDate: string | null
    tradingCompany: string
    customer: string
    constructionArea: string
    member: string
    buildingPart: string
    constructionName: string
    orderNumber: string
    innerNo: string
    steelOrderId: string | null
    millsheetItems: Array<{
      id: string
      heatNumber: string
      individualNumber: string
      pageNumber: number | null
      steelType: string
      sectionType: string
      constructionArea: string
      member: string
      buildingPart: string
      quantity: number
      size: string
      thicknessDiameter: string | null
      endorsementCompany: string | null
      endorsementDate: string | null
      endorsementCompany2: string | null
      endorsementDate2: string | null
      endorsementCompany3: string | null
      endorsementDate3: string | null
      endorsementCompany4: string | null
      endorsementDate4: string | null
      endorsementCompany5: string | null
      endorsementDate5: string | null
      endorsementCompany6: string | null
      endorsementDate6: string | null
      weightBeforeProcessing: number | null
      length: string
      rank: number
    }>
    s3Objects: Array<{ id: string; resourceType: S3ObjectResourceTypeEnum; thumbnailCount: number | null }>
    shearingCertificates: Array<{ id: string; certificateNo: string | null; s3Object: { id: string } | null }>
  }>
}

export type FetchUnlinkedMillsheetsFilterOptionsQueryVariables = Exact<{ [key: string]: never }>

export type FetchUnlinkedMillsheetsFilterOptionsQuery = {
  unlinkedMillsheetsFilterOptions: {
    manufacturers: Array<string>
    tradingCompanies: Array<string>
    customers: Array<string>
    constructionNames: Array<string>
    constructionAreas: Array<string>
    members: Array<string>
    buildingParts: Array<string>
    steelTypes: Array<string>
    steelSectionTypes: Array<string>
    submissionDates: Array<string>
    endorsementCompanies: Array<string>
  }
}

export type FetchUsersQueryVariables = Exact<{ [key: string]: never }>

export type FetchUsersQuery = {
  users: Array<{
    id: string
    name: string
    email: string
    role: UserRoleEnum
    smsMfaRequired: boolean
    createdAt: string
    signedUpAt: string | null
  }>
}

export const AdminIdentityFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminIdentityFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminIdentity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'archivedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AdminIdentityFieldsFragment, unknown>
export const CreateMillsheetsByCsvResultFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CreateMillsheetsByCsvResultFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CreateMillsheetsByCsvResult' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          { kind: 'Field', name: { kind: 'Name', value: 'success' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isEmptyLine' } },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'resultTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDuplicate' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateMillsheetsByCsvResultFieldsFragment, unknown>
export const CreateShearingCertificatesByCsvResultFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CreateShearingCertificatesByCsvResultFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CreateShearingCertificatesByCsvResult' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          { kind: 'Field', name: { kind: 'Name', value: 'success' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isEmptyLine' } },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'resultTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDuplicate' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateShearingCertificatesByCsvResultFieldsFragment, unknown>
export const MillsheetItemFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MillsheetItemFieldsFragment, unknown>
export const MillsheetDuplicateGroupFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetDuplicateGroupFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetDuplicateGroup' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'key' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmed' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheet' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'no' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Objects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'originalFileName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'items' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MillsheetDuplicateGroupFieldsFragment, unknown>
export const TenantSettingFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TenantSettingFieldsFragment, unknown>
export const MyTenantFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MyTenantFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MyTenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setting' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MyTenantFieldsFragment, unknown>
export const MeFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MeFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Me' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'imageUrl' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenant' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MyTenantFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MyTenantFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MyTenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setting' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MeFieldsFragment, unknown>
export const MyIdentityFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MyIdentityFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MyIdentity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MeFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MyTenantFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MyTenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setting' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MeFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Me' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'imageUrl' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenant' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MyTenantFields' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MyIdentityFieldsFragment, unknown>
export const NestingDiagramManageFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'NestingDiagramManageFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'NestingDiagram' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shearingCertificateItemId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'linkedCertificateNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Object' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<NestingDiagramManageFieldsFragment, unknown>
export const OrderFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceFileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectNameFixed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryDueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'remarks' } },
          { kind: 'Field', name: { kind: 'Name', value: 's3ObjectId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'uploadedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'registeredAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionModel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionError' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ocrRetryAvailableAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedByName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'itemCount' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderFieldsFragment, unknown>
export const OrderDetailFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDetailFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderFields' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extraction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'items' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'lineNo' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'size' } },
                { kind: 'Field', name: { kind: 'Name', value: 'length' } },
                { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
                { kind: 'Field', name: { kind: 'Name', value: 'plateNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
                { kind: 'Field', name: { kind: 'Name', value: 'editedAt' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceFileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectNameFixed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryDueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'remarks' } },
          { kind: 'Field', name: { kind: 'Name', value: 's3ObjectId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'uploadedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'registeredAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionModel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionError' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ocrRetryAvailableAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedByName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'itemCount' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<OrderDetailFieldsFragment, unknown>
export const ShapeNameConversionFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShapeNameConversionFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ShapeNameConversion' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'field' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'normalizedValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ShapeNameConversionFieldsFragment, unknown>
export const NestingDiagramFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'NestingDiagramFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'NestingDiagram' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shearingCertificateItemId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Object' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<NestingDiagramFieldsFragment, unknown>
export const ShearingCertificateItemFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShearingCertificateItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ShearingCertificateItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thickness' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheets' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Objects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'nestingDiagrams' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'NestingDiagramFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'NestingDiagramFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'NestingDiagram' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shearingCertificateItemId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Object' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ShearingCertificateItemFieldsFragment, unknown>
export const ShearingCertificateFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShearingCertificateFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ShearingCertificate' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuingCompanyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'items' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ShearingCertificateItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Object' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'NestingDiagramFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'NestingDiagram' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shearingCertificateItemId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Object' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShearingCertificateItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ShearingCertificateItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thickness' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheets' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Objects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'nestingDiagrams' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'NestingDiagramFields' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ShearingCertificateFieldsFragment, unknown>
export const ShearingCertificatePdfFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShearingCertificatePdfFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'S3Object' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'originalFileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'linkedShearingCertificateId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'linkedShearingCertificateNo' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ShearingCertificatePdfFieldsFragment, unknown>
export const SteelOrderCsvImportFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderCsvImportFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderCsvImport' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'summary' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalRowCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emptyRowCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'importedRowCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'errorRows' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'rowNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'message' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'skippedContractNumbers' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'skippedSiteCodeRows' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'siteCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'rowCount' } },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdByName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SteelOrderCsvImportFieldsFragment, unknown>
export const SteelOrderCsvImportRowResultFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderCsvImportRowResultFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderCsvImportRowResult' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          { kind: 'Field', name: { kind: 'Name', value: 'success' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isEmptyLine' } },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'resultTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'skippedByContractNumberError' } },
          { kind: 'Field', name: { kind: 'Name', value: 'skippedByUnknownSiteCode' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SteelOrderCsvImportRowResultFieldsFragment, unknown>
export const SteelOrderItemFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'managementNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'productName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'standard' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'itemNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'answeredDeliveryDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionNote' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckUpdatedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SteelOrderItemFieldsFragment, unknown>
export const SteelOrderFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referenceNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'category' } },
          { kind: 'Field', name: { kind: 'Name', value: 'millsheetStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isMillsheetExcluded' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outputDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'totalWeight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'millsheetWeight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderedWeight' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'items' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheets' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'no' } },
                { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Objects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'managementNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'productName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'standard' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'itemNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'answeredDeliveryDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionNote' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckUpdatedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SteelOrderFieldsFragment, unknown>
export const MillsheetFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Millsheet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'submissionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tradingCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'innerNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetItems' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificates' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Object' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<MillsheetFieldsFragment, unknown>
export const SteelOrderDetailFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderDetailFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderFields' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheets' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'managementNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'productName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'standard' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'itemNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'answeredDeliveryDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionNote' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckUpdatedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referenceNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'category' } },
          { kind: 'Field', name: { kind: 'Name', value: 'millsheetStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isMillsheetExcluded' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outputDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'totalWeight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'millsheetWeight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderedWeight' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'items' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheets' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'no' } },
                { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Objects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Millsheet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'submissionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tradingCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'innerNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetItems' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificates' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Object' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<SteelOrderDetailFieldsFragment, unknown>
export const UserFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'smsMfaRequired' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signedUpAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UserFieldsFragment, unknown>
export const PlanFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PlanFieldsFragment, unknown>
export const TenantFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Tenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'introductionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastConversationDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInterviewDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setting' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenantPlan' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'planId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'quoteNumber' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'plan' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'smsMfaRequired' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signedUpAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TenantFieldsFragment, unknown>
export const TenantSimpleFieldsFragmentDoc = {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSimpleFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Tenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'introductionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastConversationDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInterviewDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenantPlan' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'planId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'quoteNumber' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'plan' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<TenantSimpleFieldsFragment, unknown>
export const CreateInvitationDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'createInvitation' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CreateInvitationInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createInvitation' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'smsMfaRequired' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signedUpAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateInvitationMutation, CreateInvitationMutationVariables>
export const AgreeToServiceTermsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'agreeToServiceTerms' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'agreeToServiceTerms' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MeFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MyTenantFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MyTenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setting' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MeFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Me' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'imageUrl' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenant' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MyTenantFields' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<AgreeToServiceTermsMutation, AgreeToServiceTermsMutationVariables>
export const ArchiveAdminIdentityDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'archiveAdminIdentity' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ArchiveAdminIdentityInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'archiveAdminIdentity' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminIdentityFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminIdentityFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminIdentity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'archivedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ArchiveAdminIdentityMutation, ArchiveAdminIdentityMutationVariables>
export const ConfirmMillsheetDuplicateGroupDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'confirmMillsheetDuplicateGroup' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'millsheetId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'itemIds' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'confirmMillsheetDuplicateGroup' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'millsheetId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'millsheetId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'itemIds' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'itemIds' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConfirmMillsheetDuplicateGroupMutation, ConfirmMillsheetDuplicateGroupMutationVariables>
export const ConfirmOrderDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'confirmOrder' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'confirmOrder' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceFileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectNameFixed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryDueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'remarks' } },
          { kind: 'Field', name: { kind: 'Name', value: 's3ObjectId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'uploadedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'registeredAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionModel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionError' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ocrRetryAvailableAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedByName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'itemCount' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ConfirmOrderMutation, ConfirmOrderMutationVariables>
export const CreateAdminIdentityInvitationDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'createAdminIdentityInvitation' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CreateAdminIdentityInvitationInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createAdminIdentityInvitation' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminIdentityFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminIdentityFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminIdentity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'archivedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateAdminIdentityInvitationMutation, CreateAdminIdentityInvitationMutationVariables>
export const CreateAdminInvitationDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'createAdminInvitation' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'CreateAdminInvitationInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createAdminInvitation' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'smsMfaRequired' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signedUpAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateAdminInvitationMutation, CreateAdminInvitationMutationVariables>
export const CreateAdminPlanDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'createAdminPlan' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'plan' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'PlanCreateInput' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createAdminPlan' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'plan' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'plan' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateAdminPlanMutation, CreateAdminPlanMutationVariables>
export const CreateMillsheetsByCsvDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'createMillsheetsByCsv' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'csv' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'mode' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetCsvImportModeEnum' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createMillsheetsByCsv' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'csv' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'csv' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'mode' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'mode' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'CreateMillsheetsByCsvResultFields' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CreateMillsheetsByCsvResultFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CreateMillsheetsByCsvResult' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          { kind: 'Field', name: { kind: 'Name', value: 'success' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isEmptyLine' } },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'resultTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDuplicate' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateMillsheetsByCsvMutation, CreateMillsheetsByCsvMutationVariables>
export const CreateOrderFromPdfDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'createOrderFromPdf' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 's3ObjectId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'projectName' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createOrderFromPdf' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 's3ObjectId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 's3ObjectId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'projectName' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'projectName' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceFileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectNameFixed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryDueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'remarks' } },
          { kind: 'Field', name: { kind: 'Name', value: 's3ObjectId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'uploadedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'registeredAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionModel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionError' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ocrRetryAvailableAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedByName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'itemCount' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateOrderFromPdfMutation, CreateOrderFromPdfMutationVariables>
export const CreateS3ObjectDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'createS3Object' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'S3ObjectCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createS3Object' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'fileUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'fileUrlForPut' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'millsheetAutoLink' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'millsheetNo' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'millsheetConstructionName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'millsheetOrderNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'candidateConstructionNames' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'candidateOrderNumbers' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateS3ObjectMutation, CreateS3ObjectMutationVariables>
export const CreateS3ObjectThumbnailsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'createS3ObjectThumbnails' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 's3ObjectId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createS3ObjectThumbnails' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 's3ObjectId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 's3ObjectId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'fileUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailFileUrls' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateS3ObjectThumbnailsMutation, CreateS3ObjectThumbnailsMutationVariables>
export const CreateShearingCertificatesByCsvDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'createShearingCertificatesByCsv' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'csv' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'mode' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetCsvImportModeEnum' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createShearingCertificatesByCsv' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'csv' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'csv' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'mode' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'mode' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'FragmentSpread',
                  name: { kind: 'Name', value: 'CreateShearingCertificatesByCsvResultFields' },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'CreateShearingCertificatesByCsvResultFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'CreateShearingCertificatesByCsvResult' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          { kind: 'Field', name: { kind: 'Name', value: 'success' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isEmptyLine' } },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'resultTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isDuplicate' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateShearingCertificatesByCsvMutation, CreateShearingCertificatesByCsvMutationVariables>
export const CreateTenantDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'createTenant' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'tenant' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminTenantCreateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'createAdminTenant' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'tenant' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'tenant' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'smsMfaRequired' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signedUpAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Tenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'introductionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastConversationDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInterviewDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setting' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenantPlan' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'planId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'quoteNumber' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'plan' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<CreateTenantMutation, CreateTenantMutationVariables>
export const DeleteAdminPlanDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteAdminPlan' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteAdminPlan' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteAdminPlanMutation, DeleteAdminPlanMutationVariables>
export const DeleteAdminShapeNameConversionDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteAdminShapeNameConversion' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'tenantId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteAdminShapeNameConversion' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'tenantId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'tenantId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ShapeNameConversionFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShapeNameConversionFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ShapeNameConversion' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'field' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'normalizedValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteAdminShapeNameConversionMutation, DeleteAdminShapeNameConversionMutationVariables>
export const DeleteAdminUserDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteAdminUser' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteAdminUser' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteAdminUserMutation, DeleteAdminUserMutationVariables>
export const DeleteMillsheetDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteMillsheet' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteMillsheet' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Millsheet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'submissionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tradingCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'innerNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetItems' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificates' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Object' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteMillsheetMutation, DeleteMillsheetMutationVariables>
export const DeleteMillsheetItemsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteMillsheetItems' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteMillsheetItems' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'ids' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteMillsheetItemsMutation, DeleteMillsheetItemsMutationVariables>
export const DeleteNestingDiagramDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteNestingDiagram' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteNestingDiagram' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteNestingDiagramMutation, DeleteNestingDiagramMutationVariables>
export const DeleteOrderLineMatchingOrderDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteOrderLineMatchingOrder' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'DeleteOrderLineMatchingOrderInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteOrderLineMatchingOrder' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteOrderLineMatchingOrderMutation, DeleteOrderLineMatchingOrderMutationVariables>
export const DeleteS3ObjectDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteS3Object' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteS3Object' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteS3ObjectMutation, DeleteS3ObjectMutationVariables>
export const DeleteShearingCertificateDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteShearingCertificate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteShearingCertificate' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteShearingCertificateMutation, DeleteShearingCertificateMutationVariables>
export const DeleteSteelOrdersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteSteelOrders' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteSteelOrders' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'ids' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteSteelOrdersMutation, DeleteSteelOrdersMutationVariables>
export const DeleteSteelOrdersByTargetDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteSteelOrdersByTarget' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'target' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrdersBulkOperationTargetInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteSteelOrders' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'target' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'target' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteSteelOrdersByTargetMutation, DeleteSteelOrdersByTargetMutationVariables>
export const DeleteTenantDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteTenant' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteAdminTenant' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'smsMfaRequired' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signedUpAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Tenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'introductionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastConversationDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInterviewDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setting' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenantPlan' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'planId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'quoteNumber' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'plan' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteTenantMutation, DeleteTenantMutationVariables>
export const DeleteUserDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'deleteUser' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteUser' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DeleteUserMutation, DeleteUserMutationVariables>
export const DetachShearingCertificatePdfDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'detachShearingCertificatePdf' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'detachShearingCertificatePdf' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<DetachShearingCertificatePdfMutation, DetachShearingCertificatePdfMutationVariables>
export const ImportAdminOrderCsvDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'importAdminOrderCsv' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'tenantId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'csv' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'fileName' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'pdfs' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: {
                kind: 'NonNullType',
                type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderPdfMappingInput' } },
              },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importAdminOrderCsv' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'tenantId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'tenantId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'csv' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'csv' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'fileName' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'fileName' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'pdfs' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'pdfs' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'summary' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'totalRowCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdOrderCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updatedOrderCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdLineCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updatedLineCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'deletedLineCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'protectedLineCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'registeredPdfCount' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'errorRows' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'rowNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'message' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ImportAdminOrderCsvMutation, ImportAdminOrderCsvMutationVariables>
export const ImportDeliveryTableCsvDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'importDeliveryTableCsv' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'csv' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'fileName' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importDeliveryTableCsv' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'csv' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'csv' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'fileName' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'fileName' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'csvImport' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderCsvImportFields' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rowResults' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderCsvImportRowResultFields' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderCsvImportFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderCsvImport' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'summary' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalRowCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emptyRowCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'importedRowCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'errorRows' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'rowNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'message' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'skippedContractNumbers' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'skippedSiteCodeRows' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'siteCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'rowCount' } },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdByName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderCsvImportRowResultFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderCsvImportRowResult' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          { kind: 'Field', name: { kind: 'Name', value: 'success' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isEmptyLine' } },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'resultTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'skippedByContractNumberError' } },
          { kind: 'Field', name: { kind: 'Name', value: 'skippedByUnknownSiteCode' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ImportDeliveryTableCsvMutation, ImportDeliveryTableCsvMutationVariables>
export const ImportScrmCsvDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'importScrmCsv' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'csv' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'fileName' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'importScrmCsv' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'csv' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'csv' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'fileName' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'fileName' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'csvImport' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderCsvImportFields' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rowResults' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderCsvImportRowResultFields' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderCsvImportFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderCsvImport' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'summary' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalRowCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emptyRowCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'importedRowCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'errorRows' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'rowNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'message' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'skippedContractNumbers' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'skippedSiteCodeRows' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'siteCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'rowCount' } },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdByName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderCsvImportRowResultFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderCsvImportRowResult' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'index' } },
          { kind: 'Field', name: { kind: 'Name', value: 'success' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isEmptyLine' } },
          { kind: 'Field', name: { kind: 'Name', value: 'message' } },
          { kind: 'Field', name: { kind: 'Name', value: 'resultTitle' } },
          { kind: 'Field', name: { kind: 'Name', value: 'skippedByContractNumberError' } },
          { kind: 'Field', name: { kind: 'Name', value: 'skippedByUnknownSiteCode' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<ImportScrmCsvMutation, ImportScrmCsvMutationVariables>
export const PrepareAdminOrderPdfUploadDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'prepareAdminOrderPdfUpload' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'tenantId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'fileName' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'contentLength' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'prepareAdminOrderPdfUpload' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'tenantId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'tenantId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'fileName' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'fileName' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'contentLength' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'contentLength' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'fileUrlForPut' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PrepareAdminOrderPdfUploadMutation, PrepareAdminOrderPdfUploadMutationVariables>
export const PrepareOrderPdfUploadDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'prepareOrderPdfUpload' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'fileName' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'contentLength' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'prepareOrderPdfUpload' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'fileName' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'fileName' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'contentLength' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'contentLength' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'fileUrlForPut' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PrepareOrderPdfUploadMutation, PrepareOrderPdfUploadMutationVariables>
export const PreviewAdminOrderCsvDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'previewAdminOrderCsv' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'tenantId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'csv' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'previewAdminOrderCsv' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'tenantId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'tenantId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'csv' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'csv' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'projectName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sourceFileName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'itemCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<PreviewAdminOrderCsvMutation, PreviewAdminOrderCsvMutationVariables>
export const ReleaseOrderLineMatchingAllocationDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'releaseOrderLineMatchingAllocation' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ReleaseOrderLineMatchingAllocationInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'releaseOrderLineMatchingAllocation' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  ReleaseOrderLineMatchingAllocationMutation,
  ReleaseOrderLineMatchingAllocationMutationVariables
>
export const RenameOrderProjectDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'renameOrderProject' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'projectNameNormalized' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'newProjectName' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'renameOrderProject' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'projectNameNormalized' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'projectNameNormalized' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'newProjectName' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'newProjectName' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'projectName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'projectNameNormalized' } },
                { kind: 'Field', name: { kind: 'Name', value: 'updatedOrderCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'merged' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RenameOrderProjectMutation, RenameOrderProjectMutationVariables>
export const RestoreAdminIdentityDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'restoreAdminIdentity' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'RestoreAdminIdentityInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'restoreAdminIdentity' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminIdentityFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminIdentityFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminIdentity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'archivedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RestoreAdminIdentityMutation, RestoreAdminIdentityMutationVariables>
export const RetryOrderOcrDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'retryOrderOcr' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'retryOrderOcr' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceFileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectNameFixed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryDueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'remarks' } },
          { kind: 'Field', name: { kind: 'Name', value: 's3ObjectId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'uploadedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'registeredAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionModel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionError' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ocrRetryAvailableAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedByName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'itemCount' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<RetryOrderOcrMutation, RetryOrderOcrMutationVariables>
export const UnconfirmMillsheetDuplicateGroupDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'unconfirmMillsheetDuplicateGroup' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'millsheetId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'itemIds' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'unconfirmMillsheetDuplicateGroup' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'millsheetId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'millsheetId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'itemIds' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'itemIds' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  UnconfirmMillsheetDuplicateGroupMutation,
  UnconfirmMillsheetDuplicateGroupMutationVariables
>
export const UnlinkMillsheetS3ObjectsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'unlinkMillsheetS3Objects' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'millsheetId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 's3ObjectIds' } },
          type: {
            kind: 'NonNullType',
            type: {
              kind: 'ListType',
              type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
            },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'unlinkMillsheetS3Objects' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'millsheetId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'millsheetId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 's3ObjectIds' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 's3ObjectIds' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UnlinkMillsheetS3ObjectsMutation, UnlinkMillsheetS3ObjectsMutationVariables>
export const UpdateAdminIdentityRoleDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateAdminIdentityRole' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'UpdateAdminIdentityRoleInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateAdminIdentityRole' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminIdentityFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminIdentityFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminIdentity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'archivedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateAdminIdentityRoleMutation, UpdateAdminIdentityRoleMutationVariables>
export const UpdateAdminPlanDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateAdminPlan' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'plan' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'PlanUpdateInput' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateAdminPlan' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'plan' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'plan' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateAdminPlanMutation, UpdateAdminPlanMutationVariables>
export const UpdateMillsheetDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateMillsheet' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateMillsheet' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Millsheet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'submissionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tradingCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'innerNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetItems' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificates' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Object' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateMillsheetMutation, UpdateMillsheetMutationVariables>
export const UpdateMillsheetItemDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateMillsheetItem' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItemUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateMillsheetItem' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateMillsheetItemMutation, UpdateMillsheetItemMutationVariables>
export const UpdateMillsheetOrderNumberDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateMillsheetOrderNumber' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'millsheetId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'orderNumber' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateMillsheetOrderNumber' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'millsheetId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'millsheetId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'orderNumber' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'orderNumber' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateMillsheetOrderNumberMutation, UpdateMillsheetOrderNumberMutationVariables>
export const UpdateMillsheetsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateMillsheets' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetsUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateMillsheets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Millsheet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'submissionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tradingCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'innerNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetItems' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificates' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Object' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateMillsheetsMutation, UpdateMillsheetsMutationVariables>
export const UpdateNestingDiagramDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateNestingDiagram' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'individualNumber' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateNestingDiagram' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: {
                  kind: 'ObjectValue',
                  fields: [
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'id' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
                    },
                    {
                      kind: 'ObjectField',
                      name: { kind: 'Name', value: 'individualNumber' },
                      value: { kind: 'Variable', name: { kind: 'Name', value: 'individualNumber' } },
                    },
                  ],
                },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'NestingDiagramManageFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'NestingDiagramManageFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'NestingDiagram' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shearingCertificateItemId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'linkedCertificateNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Object' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateNestingDiagramMutation, UpdateNestingDiagramMutationVariables>
export const UpdateOrderFromReviewDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateOrderFromReview' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'records' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'JSON' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'expectedUpdatedAt' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'DateTime' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateOrderFromReview' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'records' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'records' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'expectedUpdatedAt' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'expectedUpdatedAt' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'summary' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'totalRowCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdOrderCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updatedOrderCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdLineCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updatedLineCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'deletedLineCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'protectedLineCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'registeredPdfCount' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'errorRows' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'rowNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'message' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateOrderFromReviewMutation, UpdateOrderFromReviewMutationVariables>
export const UpdateOrderLineMatchingItemDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateOrderLineMatchingItem' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'UpdateOrderLineMatchingItemInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateOrderLineMatchingItem' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateOrderLineMatchingItemMutation, UpdateOrderLineMatchingItemMutationVariables>
export const UpdateS3ObjectDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateS3Object' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'S3ObjectUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateS3Object' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateS3ObjectMutation, UpdateS3ObjectMutationVariables>
export const UpdateSteelOrderItemQualityCheckStatusDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateSteelOrderItemQualityCheckStatus' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'steelOrderItemId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'qualityCheckStatus' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderQualityCheckStatusEnum' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateSteelOrderItemQualityCheckStatus' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'steelOrderItemId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'steelOrderItemId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'qualityCheckStatus' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'qualityCheckStatus' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  UpdateSteelOrderItemQualityCheckStatusMutation,
  UpdateSteelOrderItemQualityCheckStatusMutationVariables
>
export const UpdateSteelOrdersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateSteelOrders' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrdersUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateSteelOrders' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateSteelOrdersMutation, UpdateSteelOrdersMutationVariables>
export const UpdateTenantDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateTenant' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'tenant' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminTenantUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateAdminTenant' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'tenant' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'tenant' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'smsMfaRequired' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signedUpAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Tenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'introductionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastConversationDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInterviewDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setting' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenantPlan' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'planId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'quoteNumber' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'plan' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateTenantMutation, UpdateTenantMutationVariables>
export const UpdateTenantSettingDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateTenantSetting' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSettingUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateTenantSetting' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateTenantSettingMutation, UpdateTenantSettingMutationVariables>
export const UpdateUserDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'updateUser' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'UserInput' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'updateUser' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'smsMfaRequired' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signedUpAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpdateUserMutation, UpdateUserMutationVariables>
export const UpsertAdminShapeNameConversionDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'upsertAdminShapeNameConversion' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminShapeNameConversionUpsertInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'upsertAdminShapeNameConversion' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ShapeNameConversionFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShapeNameConversionFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ShapeNameConversion' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'field' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'normalizedValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpsertAdminShapeNameConversionMutation, UpsertAdminShapeNameConversionMutationVariables>
export const UpsertAdminTenantSettingDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'upsertAdminTenantSetting' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'tenantSetting' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminTenantSettingUpdateInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'upsertAdminTenantSetting' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'tenantSetting' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<UpsertAdminTenantSettingMutation, UpsertAdminTenantSettingMutationVariables>
export const UpsertOrderLineMatchingAllocationDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'upsertOrderLineMatchingAllocation' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'UpsertOrderLineMatchingAllocationInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'upsertOrderLineMatchingAllocation' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  UpsertOrderLineMatchingAllocationMutation,
  UpsertOrderLineMatchingAllocationMutationVariables
>
export const FetchAdminS3ObjectDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchAdminS3Object' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminS3Object' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originalFileName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'extension' } },
                { kind: 'Field', name: { kind: 'Name', value: 'contentType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'contentLength' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'imageWidth' } },
                { kind: 'Field', name: { kind: 'Name', value: 'imageHeight' } },
                { kind: 'Field', name: { kind: 'Name', value: 'fileUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailFileUrls' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchAdminS3ObjectQuery, FetchAdminS3ObjectQueryVariables>
export const FetchAdminShapeNameConversionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchAdminShapeNameConversions' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'tenantId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminShapeNameConversions' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'tenantId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'tenantId' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ShapeNameConversionFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShapeNameConversionFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ShapeNameConversion' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'field' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'normalizedValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchAdminShapeNameConversionsQuery, FetchAdminShapeNameConversionsQueryVariables>
export const FetchAdminTenantsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchAdminTenants' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminTenants' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'tenants' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'smsMfaRequired' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signedUpAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Tenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'introductionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastConversationDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInterviewDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setting' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenantPlan' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'planId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'quoteNumber' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'plan' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchAdminTenantsQuery, FetchAdminTenantsQueryVariables>
export const FetchDashboardDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchDashboard' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'dashboard' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'millsheetsCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'tenantPlan' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'quoteNumber' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'plan' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchDashboardQuery, FetchDashboardQueryVariables>
export const FetchMeDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchMe' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'me' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MeFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MyTenantFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MyTenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setting' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MeFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Me' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'imageUrl' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenant' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MyTenantFields' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchMeQuery, FetchMeQueryVariables>
export const FetchMillsheetDuplicateGroupCountDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchMillsheetDuplicateGroupCount' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'unconfirmedOnly' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetDuplicateGroupCount' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'unconfirmedOnly' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'unconfirmedOnly' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchMillsheetDuplicateGroupCountQuery, FetchMillsheetDuplicateGroupCountQueryVariables>
export const FetchMillsheetDuplicateGroupsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchMillsheetDuplicateGroups' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetDuplicateGroupsInput' } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'unconfirmedOnly' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetDuplicateGroups' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetDuplicateGroupFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetDuplicateGroupCount' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'unconfirmedOnly' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'unconfirmedOnly' } },
              },
            ],
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetDuplicateGroupFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetDuplicateGroup' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'key' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmed' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheet' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'no' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Objects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'originalFileName' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'items' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchMillsheetDuplicateGroupsQuery, FetchMillsheetDuplicateGroupsQueryVariables>
export const FetchMillsheetItemsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchMillsheetItems' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'heatNumber' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetItems' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'heatNumber' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'heatNumber' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } },
                { kind: 'Field', name: { kind: 'Name', value: 'millsheetNo' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchMillsheetItemsQuery, FetchMillsheetItemsQueryVariables>
export const FetchMillsheetUniqueValuesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchMillsheetUniqueValues' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetUniqueValues' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerNames' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionTypes' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchMillsheetUniqueValuesQuery, FetchMillsheetUniqueValuesQueryVariables>
export const FetchMillsheetsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchMillsheets' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheets' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetFields' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelOrderReferenceNumber' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Millsheet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'submissionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tradingCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'innerNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetItems' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificates' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Object' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchMillsheetsQuery, FetchMillsheetsQueryVariables>
export const FetchMillsheetsFilterOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchMillsheetsFilterOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetsFilterOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturers' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tradingCompanies' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customers' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionNames' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionAreas' } },
                { kind: 'Field', name: { kind: 'Name', value: 'members' } },
                { kind: 'Field', name: { kind: 'Name', value: 'buildingParts' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelSectionTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submissionDates' } },
                { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompanies' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchMillsheetsFilterOptionsQuery, FetchMillsheetsFilterOptionsQueryVariables>
export const FetchMyIdentityDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchMyIdentity' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'myIdentity' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MyIdentityFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MyTenantFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MyTenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setting' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MeFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Me' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'imageUrl' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenant' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MyTenantFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MyIdentityFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MyIdentity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MeFields' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchMyIdentityQuery, FetchMyIdentityQueryVariables>
export const FetchMyTenantDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchMyTenantDetail' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'me' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'tenant' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'tenantPlan' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'quoteNumber' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'plan' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchMyTenantDetailQuery, FetchMyTenantDetailQueryVariables>
export const FetchNestingDiagramsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchNestingDiagrams' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'nestingDiagrams' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'NestingDiagramManageFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'NestingDiagramManageFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'NestingDiagram' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shearingCertificateItemId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'linkedCertificateNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Object' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchNestingDiagramsQuery, FetchNestingDiagramsQueryVariables>
export const FetchOrderDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchOrder' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'order' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderDetailFields' } }],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderOcrEnabled' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceFileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectNameFixed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryDueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'remarks' } },
          { kind: 'Field', name: { kind: 'Name', value: 's3ObjectId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'uploadedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'registeredAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionModel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionError' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ocrRetryAvailableAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedByName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'itemCount' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderDetailFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderFields' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extraction' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'items' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'lineNo' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'size' } },
                { kind: 'Field', name: { kind: 'Name', value: 'length' } },
                { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
                { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
                { kind: 'Field', name: { kind: 'Name', value: 'plateNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
                { kind: 'Field', name: { kind: 'Name', value: 'editedAt' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchOrderQuery, FetchOrderQueryVariables>
export const FetchOrderLineMatchingPdfUrlDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchOrderLineMatchingPdfUrl' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'tenantId' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 's3ObjectId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderLineMatchingPdfUrl' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'tenantId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'tenantId' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 's3ObjectId' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 's3ObjectId' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchOrderLineMatchingPdfUrlQuery, FetchOrderLineMatchingPdfUrlQueryVariables>
export const FetchOrderLineMatchingProjectDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchOrderLineMatchingProject' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderLineMatchingProjectInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderLineMatchingProject' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'projectName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'orderCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'matrix' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'allocatedWeight' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'orders' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'orderedAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'deliveryDueAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 's3ObjectId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sourceFileName' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'items' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'lineNo' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'size' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'length' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'plateNumber' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'arrivalStatus' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'allocatedQuantity' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'allocatedWeight' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'weightDiff' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'weightDiffRate' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'needsReview' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'editedAt' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'reviewReasons' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'allocations' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'millsheetId' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'millsheetItemId' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'certificateNumber' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'itemNo' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'originalQuantity' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'method' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'confidence' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'pdfs' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 's3ObjectId' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'page' } },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchOrderLineMatchingProjectQuery, FetchOrderLineMatchingProjectQueryVariables>
export const FetchOrderLineMatchingProjectsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchOrderLineMatchingProjects' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderLineMatchingProjectsInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderLineMatchingProjects' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'projects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'projectKey' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'projectName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'orderCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'itemCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'arrivedItemCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'reviewItemCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'notArrivedItemCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalWeight' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'allocatedWeight' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchOrderLineMatchingProjectsQuery, FetchOrderLineMatchingProjectsQueryVariables>
export const FetchOrderLineMatchingUnlinkedFilterOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchOrderLineMatchingUnlinkedFilterOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderLineMatchingUnlinkedFilterOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturers' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tradingCompanies' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customers' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionNames' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionAreas' } },
                { kind: 'Field', name: { kind: 'Name', value: 'members' } },
                { kind: 'Field', name: { kind: 'Name', value: 'buildingParts' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelSectionTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submissionDates' } },
                { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompanies' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FetchOrderLineMatchingUnlinkedFilterOptionsQuery,
  FetchOrderLineMatchingUnlinkedFilterOptionsQueryVariables
>
export const FetchOrderLineMatchingUnlinkedItemsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchOrderLineMatchingUnlinkedItems' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'OrderLineMatchingUnlinkedInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderLineMatchingUnlinkedItems' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'items' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'millsheetId' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'itemNo' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'certificateNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'size' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'length' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'tradingCompany' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalOriginalQuantity' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'usedOriginalQuantity' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'remainingOriginalQuantity' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'allocatableOriginalQuantity' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'totalWeight' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'usedWeight' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'remainingWeight' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'allocationCount' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FetchOrderLineMatchingUnlinkedItemsQuery,
  FetchOrderLineMatchingUnlinkedItemsQueryVariables
>
export const FetchOrderOcrEnabledDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchOrderOcrEnabled' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [{ kind: 'Field', name: { kind: 'Name', value: 'orderOcrEnabled' } }],
      },
    },
  ],
} as unknown as DocumentNode<FetchOrderOcrEnabledQuery, FetchOrderOcrEnabledQueryVariables>
export const FetchOrderPdfUrlDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchOrderPdfUrl' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderPdfUrl' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchOrderPdfUrlQuery, FetchOrderPdfUrlQueryVariables>
export const FetchOrderProjectNamesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchOrderProjectNames' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'includeUnregistered' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orderProjectNames' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'includeUnregistered' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'includeUnregistered' } },
              },
            ],
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchOrderProjectNamesQuery, FetchOrderProjectNamesQueryVariables>
export const FetchOrdersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchOrders' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
          type: {
            kind: 'ListType',
            type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'page' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'pageSize' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'orders' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'ids' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'page' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'page' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'pageSize' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'pageSize' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'orders' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'OrderFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'OrderFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Order' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceFileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'projectNameFixed' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryDueAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'deliveryPlace' } },
          { kind: 'Field', name: { kind: 'Name', value: 'remarks' } },
          { kind: 'Field', name: { kind: 'Name', value: 's3ObjectId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'note' } },
          { kind: 'Field', name: { kind: 'Name', value: 'uploadedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'registeredAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionModel' } },
          { kind: 'Field', name: { kind: 'Name', value: 'extractionError' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ocrRetryAvailableAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'confirmedByName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'itemCount' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchOrdersQuery, FetchOrdersQueryVariables>
export const FetchPagedAdminIdentitiesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchPagedAdminIdentities' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'PagedAdminIdentitiesInput' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pagedAdminIdentities' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'items' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AdminIdentityFields' } }],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AdminIdentityFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AdminIdentity' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'archivedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchPagedAdminIdentitiesQuery, FetchPagedAdminIdentitiesQueryVariables>
export const FetchPagedMillsheetItemsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchPagedMillsheetItems' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'heatNumber' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'page' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'pageSize' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pagedMillsheetItems' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'heatNumber' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'heatNumber' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'page' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'page' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'pageSize' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'pageSize' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'millsheetItems' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'millsheetNo' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchPagedMillsheetItemsQuery, FetchPagedMillsheetItemsQueryVariables>
export const FetchPagedMillsheetsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchPagedMillsheets' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetsSearchInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pagedMillsheets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'millsheets' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetFields' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'steelOrderReferenceNumber' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Millsheet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'submissionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tradingCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'innerNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetItems' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificates' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Object' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchPagedMillsheetsQuery, FetchPagedMillsheetsQueryVariables>
export const FetchPagedNestingDiagramsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchPagedNestingDiagrams' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 's3ObjectIds' } },
          type: {
            kind: 'ListType',
            type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'page' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'pageSize' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pagedNestingDiagrams' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 's3ObjectIds' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 's3ObjectIds' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'page' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'page' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'pageSize' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'pageSize' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'nestingDiagrams' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'NestingDiagramManageFields' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'NestingDiagramManageFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'NestingDiagram' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shearingCertificateItemId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'linkedCertificateNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Object' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchPagedNestingDiagramsQuery, FetchPagedNestingDiagramsQueryVariables>
export const FetchPagedS3ObjectsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchPagedS3Objects' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
          type: {
            kind: 'ListType',
            type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'notConnectedOnly' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'resourceType' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'S3ObjectResourceTypeEnum' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'page' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'pageSize' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pagedS3Objects' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'ids' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'notConnectedOnly' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'notConnectedOnly' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'resourceType' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'resourceType' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'page' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'page' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'pageSize' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'pageSize' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Objects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'originalFileName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'extension' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'contentType' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'contentLength' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'imageWidth' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'imageHeight' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'fileUrl' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'thumbnailFileUrls' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'linkedMillsheets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'no' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchPagedS3ObjectsQuery, FetchPagedS3ObjectsQueryVariables>
export const FetchPagedShearingCertificatePdfsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchPagedShearingCertificatePdfs' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
          type: {
            kind: 'ListType',
            type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'resourceType' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'S3ObjectResourceTypeEnum' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'page' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'pageSize' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pagedS3Objects' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'ids' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'resourceType' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'resourceType' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'page' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'page' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'pageSize' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'pageSize' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Objects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ShearingCertificatePdfFields' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShearingCertificatePdfFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'S3Object' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'originalFileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'linkedShearingCertificateId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'linkedShearingCertificateNo' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchPagedShearingCertificatePdfsQuery, FetchPagedShearingCertificatePdfsQueryVariables>
export const FetchPagedShearingCertificatesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchPagedShearingCertificates' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'ShearingCertificatesSearchInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pagedShearingCertificates' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'shearingCertificates' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'ShearingCertificateFields' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'NestingDiagramFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'NestingDiagram' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shearingCertificateItemId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Object' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShearingCertificateItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ShearingCertificateItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thickness' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheets' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Objects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'nestingDiagrams' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'NestingDiagramFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShearingCertificateFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ShearingCertificate' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuingCompanyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'items' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ShearingCertificateItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Object' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchPagedShearingCertificatesQuery, FetchPagedShearingCertificatesQueryVariables>
export const FetchPagedSteelOrderCsvImportsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchPagedSteelOrderCsvImports' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'page' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'pageSize' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Int' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pagedSteelOrderCsvImports' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'page' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'page' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'pageSize' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'pageSize' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'steelOrderCsvImports' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderCsvImportFields' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderCsvImportFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderCsvImport' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'summary' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalRowCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emptyRowCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'importedRowCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'errorRows' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'rowNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'message' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'skippedContractNumbers' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'skippedSiteCodeRows' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'siteCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'rowCount' } },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdByName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchPagedSteelOrderCsvImportsQuery, FetchPagedSteelOrderCsvImportsQueryVariables>
export const FetchPagedUnlinkedMillsheetsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchPagedUnlinkedMillsheets' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetsSearchInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pagedUnlinkedMillsheets' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'millsheets' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetFields' } }],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Millsheet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'submissionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tradingCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'innerNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetItems' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificates' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Object' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchPagedUnlinkedMillsheetsQuery, FetchPagedUnlinkedMillsheetsQueryVariables>
export const FetchAdminPlansDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchAdminPlans' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminPlans' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchAdminPlansQuery, FetchAdminPlansQueryVariables>
export const FetchS3ObjectsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchS3Objects' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
          type: {
            kind: 'ListType',
            type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
          },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'notConnectedOnly' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'resourceType' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'S3ObjectResourceTypeEnum' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'ids' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'ids' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'notConnectedOnly' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'notConnectedOnly' } },
              },
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'resourceType' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'resourceType' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'originalFileName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'extension' } },
                { kind: 'Field', name: { kind: 'Name', value: 'contentType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'contentLength' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'imageWidth' } },
                { kind: 'Field', name: { kind: 'Name', value: 'imageHeight' } },
                { kind: 'Field', name: { kind: 'Name', value: 'fileUrl' } },
                { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailFileUrls' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'linkedMillsheets' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'no' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchS3ObjectsQuery, FetchS3ObjectsQueryVariables>
export const FetchShapeNameConversionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchShapeNameConversions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shapeNameConversions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ShapeNameConversionFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShapeNameConversionFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ShapeNameConversion' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'field' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sourceValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'normalizedValue' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'updatedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchShapeNameConversionsQuery, FetchShapeNameConversionsQueryVariables>
export const FetchShearingCertificatePdfsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchShearingCertificatePdfs' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'resourceType' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'S3ObjectResourceTypeEnum' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'resourceType' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'resourceType' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ShearingCertificatePdfFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShearingCertificatePdfFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'S3Object' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'originalFileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'linkedShearingCertificateId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'linkedShearingCertificateNo' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchShearingCertificatePdfsQuery, FetchShearingCertificatePdfsQueryVariables>
export const FetchShearingCertificateUniqueValuesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchShearingCertificateUniqueValues' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificateUniqueValues' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'issuingCompanyNames' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionNames' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerNames' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FetchShearingCertificateUniqueValuesQuery,
  FetchShearingCertificateUniqueValuesQueryVariables
>
export const FetchShearingCertificatesDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchShearingCertificates' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificates' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ShearingCertificateFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'NestingDiagramFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'NestingDiagram' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'shearingCertificateItemId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Object' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShearingCertificateItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ShearingCertificateItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thickness' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheets' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Objects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'nestingDiagrams' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'NestingDiagramFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ShearingCertificateFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'ShearingCertificate' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuingCompanyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'items' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ShearingCertificateItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Object' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchShearingCertificatesQuery, FetchShearingCertificatesQueryVariables>
export const FetchShearingCertificatesFilterOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchShearingCertificatesFilterOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificatesFilterOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'issuingCompanies' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionNames' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerNames' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FetchShearingCertificatesFilterOptionsQuery,
  FetchShearingCertificatesFilterOptionsQueryVariables
>
export const FetchSteelOrderCsvImportsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchSteelOrderCsvImports' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'steelOrderCsvImports' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderCsvImportFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderCsvImportFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderCsvImport' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'kind' } },
          { kind: 'Field', name: { kind: 'Name', value: 'source' } },
          { kind: 'Field', name: { kind: 'Name', value: 'fileName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'summary' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalRowCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'emptyRowCount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'importedRowCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'errorRows' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'rowNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'message' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'skippedContractNumbers' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'skippedSiteCodeRows' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'siteCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'rowCount' } },
                    ],
                  },
                },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'createdByName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchSteelOrderCsvImportsQuery, FetchSteelOrderCsvImportsQueryVariables>
export const FetchSteelOrderDetailDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchSteelOrderDetail' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrdersSearchInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'steelOrders' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'steelOrders' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderDetailFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'managementNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'productName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'standard' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'itemNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'answeredDeliveryDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionNote' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckUpdatedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referenceNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'category' } },
          { kind: 'Field', name: { kind: 'Name', value: 'millsheetStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isMillsheetExcluded' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outputDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'totalWeight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'millsheetWeight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderedWeight' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'items' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheets' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'no' } },
                { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Objects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Millsheet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'submissionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tradingCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'innerNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetItems' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificates' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Object' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderDetailFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderFields' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheets' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetFields' } }],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchSteelOrderDetailQuery, FetchSteelOrderDetailQueryVariables>
export const FetchSteelOrdersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchSteelOrders' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrdersSearchInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'steelOrders' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'steelOrders' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrderItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'managementNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'productName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'standard' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'itemNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'answeredDeliveryDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionNote' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckUpdatedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'SteelOrderFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrder' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'referenceNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'category' } },
          { kind: 'Field', name: { kind: 'Name', value: 'millsheetStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isMillsheetExcluded' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'qualityCheckStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'outputDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'importedAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'totalWeight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'millsheetWeight' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderedWeight' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'items' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'SteelOrderItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheets' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'no' } },
                { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Objects' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchSteelOrdersQuery, FetchSteelOrdersQueryVariables>
export const FetchSteelOrdersForPrintDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchSteelOrdersForPrint' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
          type: {
            kind: 'NonNullType',
            type: { kind: 'NamedType', name: { kind: 'Name', value: 'SteelOrdersSearchInput' } },
          },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'steelOrders' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'input' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'input' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'totalCount' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'steelOrders' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'referenceNumber' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'supplierName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'category' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'millsheets' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetFields' } }],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Millsheet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'submissionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tradingCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'innerNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetItems' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificates' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Object' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchSteelOrdersForPrintQuery, FetchSteelOrdersForPrintQueryVariables>
export const FetchTenantDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchTenant' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminTenant' },
            arguments: [
              {
                kind: 'Argument',
                name: { kind: 'Name', value: 'id' },
                value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
              },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSettingFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TenantSetting' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetSortConfig' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'orderLineMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderMatchingEnabled' } },
          { kind: 'Field', name: { kind: 'Name', value: 'scrmSiteCode' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'smsMfaRequired' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signedUpAt' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Tenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'introductionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastConversationDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInterviewDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'setting' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSettingFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenantPlan' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'planId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'quoteNumber' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'plan' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchTenantQuery, FetchTenantQueryVariables>
export const FetchTenantsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchTenants' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'adminTenants' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'total' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'tenants' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TenantSimpleFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'PlanFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Plan' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'serviceTerms' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TenantSimpleFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Tenant' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'companyName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'prefecture' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'memo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'introductionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastConversationDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lastInterviewDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'tenantPlan' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'planId' } },
                { kind: 'Field', name: { kind: 'Name', value: 'quoteNumber' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'plan' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'PlanFields' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchTenantsQuery, FetchTenantsQueryVariables>
export const FetchUnlinkedMillsheetsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchUnlinkedMillsheets' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'unlinkedMillsheets' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetItemFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'MillsheetItem' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'heatNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'individualNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'pageNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'sectionType' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'quantity' } },
          { kind: 'Field', name: { kind: 'Name', value: 'size' } },
          { kind: 'Field', name: { kind: 'Name', value: 'thicknessDiameter' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate2' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate3' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate4' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate5' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompany6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'endorsementDate6' } },
          { kind: 'Field', name: { kind: 'Name', value: 'weightBeforeProcessing' } },
          { kind: 'Field', name: { kind: 'Name', value: 'length' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rank' } },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'MillsheetFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'Millsheet' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tenantId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'no' } },
          { kind: 'Field', name: { kind: 'Name', value: 'manufacturerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'issuedDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'submissionDate' } },
          { kind: 'Field', name: { kind: 'Name', value: 'tradingCompany' } },
          { kind: 'Field', name: { kind: 'Name', value: 'customer' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionArea' } },
          { kind: 'Field', name: { kind: 'Name', value: 'member' } },
          { kind: 'Field', name: { kind: 'Name', value: 'buildingPart' } },
          { kind: 'Field', name: { kind: 'Name', value: 'constructionName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'orderNumber' } },
          { kind: 'Field', name: { kind: 'Name', value: 'innerNo' } },
          { kind: 'Field', name: { kind: 'Name', value: 'steelOrderId' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'millsheetItems' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'MillsheetItemFields' } }],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 's3Objects' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'resourceType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'thumbnailCount' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'shearingCertificates' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'certificateNo' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 's3Object' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchUnlinkedMillsheetsQuery, FetchUnlinkedMillsheetsQueryVariables>
export const FetchUnlinkedMillsheetsFilterOptionsDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchUnlinkedMillsheetsFilterOptions' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'unlinkedMillsheetsFilterOptions' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'manufacturers' } },
                { kind: 'Field', name: { kind: 'Name', value: 'tradingCompanies' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customers' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionNames' } },
                { kind: 'Field', name: { kind: 'Name', value: 'constructionAreas' } },
                { kind: 'Field', name: { kind: 'Name', value: 'members' } },
                { kind: 'Field', name: { kind: 'Name', value: 'buildingParts' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'steelSectionTypes' } },
                { kind: 'Field', name: { kind: 'Name', value: 'submissionDates' } },
                { kind: 'Field', name: { kind: 'Name', value: 'endorsementCompanies' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode<
  FetchUnlinkedMillsheetsFilterOptionsQuery,
  FetchUnlinkedMillsheetsFilterOptionsQueryVariables
>
export const FetchUsersDocument = {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'fetchUsers' },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'users' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserFields' } }],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserFields' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'User' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'role' } },
          { kind: 'Field', name: { kind: 'Name', value: 'smsMfaRequired' } },
          { kind: 'Field', name: { kind: 'Name', value: 'createdAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'signedUpAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode<FetchUsersQuery, FetchUsersQueryVariables>
